/**
 * 채널 테마 SSOT — "썰채널" 브랜딩을 한 곳에서 커스터마이즈한다.
 * ─────────────────────────────────────────────
 * 각 스타일(썰레몬·공감썰·썰씨네·재다·군림보·숏마루·썰톡·건강썰·부추전)의 "채널 정체성"
 * (채널명·이모지·대표/보조/강조 색·채널아트)만 분리해 모은 레지스트리. 렌더 골격(좌표/연출/페이싱)은
 * 각 템플릿이 고정으로 들고, "브랜딩"만 여기서 주입·교체한다.
 *
 * → 채널명을 바꾸거나(썰레몬→썰핑크) 대표/윤곽 색을 바꾸면 이 한 곳만 갈아끼우면
 *   썸네일·카탈로그 라벨·실제 영상 렌더에 전부 반영된다(단일 진실).
 * → 추후 테넌트별 커스텀(tenant_features.config) 이나 채널아트 업로드도 이 타입을 그대로 확장한다.
 *
 * 적용 지점:
 *  - story 계열(StoryTemplate): header.{title,bg,line,emoji,accent} 를 이 테마로 구성(story-template.ts).
 *  - spec  계열(Listicle/Gunrimbo/News/Community/Board/Health): theme 색 + 채널명을 이 테마로 구성.
 *  - 카탈로그(story-styles.ts / style-specs.ts 의 label·emoji): 채널명/이모지를 이 테마에서 가져온다.
 */
import { COLOR_CUT_STYLE_LABEL, SHOWREEL_STYLE_LABEL } from "./motion-stage-family";
import {
  REGIONAL_PROFILE_THEMES,
  REGIONAL_STYLE_SPECS,
  isRegionalStyleId,
} from "./regional-ssul-catalog";
export type ChannelTheme = {
  /** 채널명 — 채널명 바가 있는 계열(썰레몬류·재다·부추전)은 렌더에, 전 계열 카탈로그 라벨에 쓰인다. */
  name: string;
  /** 채널 이모지 — 상단바/카탈로그 라벨에 표시 */
  emoji: string;
  /**
   * 1차/대표색.
   *  - 썰레몬류: 상단 헤더 바 배경 / 재다: 파란 헤더 / 군림보·숏마루: 화면 배경
   *  - 썰톡: 빨강 헤더 / 부추전: 파스텔 블루 헤더 / 건강썰: (헤더 없음 — 미사용)
   */
  barColor: string;
  /**
   * 2차/보조색.
   *  - 썰레몬류: 헤더 하단 테두리 / 재다: 회색 제목바 / 군림보: 자막 외곽선 / 숏마루: 제목 밑줄
   *  - 썰톡: 태그 배경 / 부추전: 제목바 배경 / 건강썰: 빨강 CTA
   */
  borderColor: string;
  /**
   * 강조색.
   *  - 썰레몬류: 뒤로가기 화살표/시네마틱 채널명 / 재다: 로고 / 군림보·숏마루: 노랑 강조
   *  - 썰톡: 댓글 작성자(빨강) / 부추전: 헤더 글자 / 건강썰: 노랑 키워드
   */
  accentColor: string;
  /** (옵션) 채널 아트/배너 이미지 URL — 추후 업로드 연동 자리. 미지정이면 미사용. */
  channelArt?: string;
};

const REGIONAL_CHANNEL_THEMES: Record<string, ChannelTheme> = Object.fromEntries(
  REGIONAL_STYLE_SPECS.map((s) => [s.id, {
    name: s.label,
    emoji: s.emoji,
    ...REGIONAL_PROFILE_THEMES[s.voiceProfileId],
  }]),
);

/**
 * 스타일 id → 채널 테마. all-styles.ts / story-styles.ts / style-specs.ts 의 id 와 일치(9종).
 * 미등록 id 는 getChannelTheme 가 undefined 를 반환 → 각 템플릿이 내장 기본값으로 폴백한다.
 *
 * ★테스트(회장님 2026-06-20): seolemon 을 "썰핑크 + 분홍 상단바"로 교체.
 *   커스텀 구조가 썸네일·카탈로그·렌더에 일관 반영되는지 증명한다(나머지는 기존값 유지).
 */
export const CHANNEL_THEMES: Record<string, ChannelTheme> = {
  // ── story 계열(StoryTemplate) ──
  seolemon: {
    name: "썰핑크",
    emoji: "🩷",
    barColor: "#FBD6E4",   // 연분홍 상단바
    borderColor: "#F4A8C7", // 분홍 윤곽선
    accentColor: "#C03D77", // 진분홍 화살표/강조
  },
  // ── 썰핑크 복제 8종(2026-06-22, 회장님 msg 12599) ──
  //   레이아웃·연출(makeSeolemonTemplate)은 썰핑크와 동일. 채널 정체성(배너색·아이콘)과
  //   가운데 일러스트 화풍(story-styles.image.artStyle)만 채널별로 분화한다.
  //   bar=밝은 헤더 배경, border=중간 윤곽, accent=진한 글자/강조(흰 제목 가독성 위해 bar 는 밝게).
  ssulmidlife: {
    name: "썰중년",
    emoji: "☕",
    barColor: "#EBDFC9",   // 베이지 상단바
    borderColor: "#CBB68C", // 브라운 윤곽선
    accentColor: "#7A5C32", // 진브라운 강조
  },
  ssulghost: {
    name: "썰망령",
    emoji: "👻",
    barColor: "#DCD3E8",   // 연보라 상단바
    borderColor: "#9B86C0", // 보라 윤곽선
    accentColor: "#3E2D63", // 진보라먹 강조
  },
  // 납량특집 무서운 화풍 3종(2026-07-01, 회장님 msg 13077)
  ssuljunji: {
    name: "썰준지",
    emoji: "🌀",
    barColor: "#DADADA",   // 흑백 밝은 회색 상단바
    borderColor: "#7A7A7A", // 회색 윤곽선
    accentColor: "#141414", // 먹빛 흑백 강조
  },
  ssulseuseu: {
    name: "썰스스",
    emoji: "💀",
    barColor: "#C9D6D3",   // 청록먹 연톤 상단바
    borderColor: "#6E8783", // 청록 윤곽선
    accentColor: "#122F2B", // 진청록먹 강조
  },
  ssulgothic: {
    name: "썰고딕",
    emoji: "🕯️",
    barColor: "#D8CBCE",   // 고딕 와인먹 연톤 상단바
    borderColor: "#8A6B72", // 와인 윤곽선
    accentColor: "#361420", // 진와인먹 강조
  },
  // 시니어 감성 화풍 4종(2026-07-02, 썰중년 파생)
  seolpogun: {
    name: "썰포근",
    emoji: "🧡",
    barColor: "#FCE9D6",   // 따뜻 크림오렌지 상단바
    borderColor: "#E9B27E", // 오렌지 윤곽선
    accentColor: "#B5651D", // 진오렌지 강조
  },
  seoltteurak: {
    name: "썰뜨락",
    emoji: "🧶",
    barColor: "#EBE1CF",   // 뜨개 베이지 상단바
    borderColor: "#C2A575", // 브라운 윤곽선
    accentColor: "#7A5A34", // 진브라운 강조
  },
  seoldanjjak: {
    name: "썰단짝",
    emoji: "🧸",
    barColor: "#DBEFEA",   // 팝 민트 상단바
    borderColor: "#86CBBB", // 민트 윤곽선
    accentColor: "#2C7A68", // 진민트 강조
  },
  seolnoeul: {
    name: "썰노을",
    emoji: "🌅",
    barColor: "#F1E0E8",   // 노을 라벤더피치 상단바
    borderColor: "#CE9EBC", // 라벤더핑크 윤곽선
    accentColor: "#7E3E64", // 진자주 강조
  },
  seolbomnal: {
    name: "썰봄날",
    emoji: "🌸",
    barColor: "#FDE7EF",   // 봄 벚꽃 연분홍 상단바
    borderColor: "#F2ABC5", // 벚꽃핑크 윤곽선
    accentColor: "#C14E77", // 진분홍 강조
  },
  seolchueok: {
    name: "썰추억",
    emoji: "📖",
    barColor: "#FBEBC9",   // 크레용 크림옐로 상단바
    borderColor: "#E8C07A", // 크레용 골드 윤곽선
    accentColor: "#B07C2E", // 진갈금 강조
  },
  seoldambaek: {
    name: "썰담백",
    emoji: "🍃",
    barColor: "#E8F3E1",   // 산뜻 세이지 상단바
    borderColor: "#AFD498", // 라이트그린 윤곽선
    accentColor: "#4C8A3A", // 진초록 강조
  },
  seolvintage: {
    name: "썰빈티지",
    emoji: "🎨",
    barColor: "#DCE6F5",   // 레트로 블루 상단바
    borderColor: "#93B4E0", // 소프트블루 윤곽선
    accentColor: "#37568F", // 잉크네이비 강조
  },
  // 실사 비율 감성 일러스트 독립 템플릿 2종(2026-07-02, msg 1078)
  ssulmovie: {
    name: "썰즈니",
    emoji: "🎬",
    barColor: "#DCE7FA",   // 극장 스크린 라이트블루 상단바
    borderColor: "#8FB0E8", // 시네마 블루 윤곽선
    accentColor: "#274690", // 딥블루 강조
  },
  ssulsuchae: {
    name: "썰브리",
    emoji: "🎨",
    barColor: "#E3EFD8",   // 수채 연둣빛 상단바
    borderColor: "#A9CB92", // 세이지그린 윤곽선
    accentColor: "#4E7A3F", // 짙은 풀색 강조
  },
  // 실사 비율 감성 템플릿 2차 4종(2026-07-02, msg 1082)
  ssulflat: {
    name: "썰플랫",
    emoji: "🖼️",
    barColor: "#E7ECF1",   // 모던 라이트그레이 상단바
    borderColor: "#A7B4C2", // 쿨그레이 윤곽선
    accentColor: "#37506B", // 슬레이트 강조
  },
  ssulcomic: {
    name: "썰코믹",
    emoji: "💥",
    barColor: "#FCE9C7",   // 팝 크림옐로 상단바
    borderColor: "#F0B24B", // 팝 오렌지 윤곽선
    accentColor: "#C22B2B", // 코믹 레드 강조
  },
  ssulcrepas: {
    name: "썰크레파스",
    emoji: "🖍️",
    barColor: "#FBE6D6",   // 크레용 살구 상단바
    borderColor: "#EEB088", // 크레용 오렌지 윤곽선
    accentColor: "#C25A3C", // 진살구 강조
  },
  ssulneon: {
    name: "썰네온",
    emoji: "💠",
    barColor: "#241A38",   // 네온 딥퍼플 상단바(어두운 밤)
    borderColor: "#7A4BD6", // 네온 바이올렛 윤곽선
    accentColor: "#18E0D6", // 네온 시안 강조
  },
  // 독립 신규 스타일 4종(2026-07-03, msg 1104)
  ssulmuk: {
    name: "썰묵",
    emoji: "🖌️",
    barColor: "#EAE7E0",   // 한지 아이보리 상단바
    borderColor: "#A29C90", // 먹회색 윤곽선
    accentColor: "#2A2622", // 진먹 강조
  },
  ssulclay: {
    name: "썰클레이",
    emoji: "🧱",
    barColor: "#F5E6D6",   // 테라코타 크림 상단바
    borderColor: "#DBAF8C", // 점토 살구 윤곽선
    accentColor: "#9C5A38", // 진테라코타 강조
  },
  ssulanime: {
    name: "썰아니메",
    emoji: "📺",
    barColor: "#E2ECF8",   // 청춘 하늘 상단바
    borderColor: "#9DBFEA", // 소프트블루 윤곽선
    accentColor: "#3C64A8", // 애니 블루 강조
  },
  ssulfilm: {
    name: "썰필름",
    emoji: "📷",
    barColor: "#ECE4D3",   // 세피아 크림 상단바
    borderColor: "#C7B189", // 빈티지 카키 윤곽선
    accentColor: "#6A5334", // 진세피아 강조
  },
  // 픽셀 레트로 게임 독립 스타일(2026-07-03, msg 1130)
  ssulpixel: {
    name: "썰픽셀",
    emoji: "👾",
    barColor: "#E7E3F7",   // 연보라 픽셀 상단바
    borderColor: "#9A86E0", // 레트로 라벤더 윤곽선
    accentColor: "#5E35C7", // 레트로 퍼플 강조
  },
  // 세기말 아포칼립스 독립 스타일(2026-07-03, msg 1142)
  ssulpocalypse: {
    name: "썰포칼립스",
    emoji: "☢️",
    barColor: "#DBD5C9",   // 잿빛 크림 상단바
    borderColor: "#8A7A5E", // 녹슨 카키 윤곽선
    accentColor: "#B5502A", // 종말 러스트 오렌지 강조
  },
  // 대괴수 독립 스타일(2026-07-03, msg 1148)
  ssulkaiju: {
    name: "썰괴수",
    emoji: "🦖",
    barColor: "#D6DEE2",   // 스톰 그레이 상단바
    borderColor: "#6E8894", // 스틸 블루 윤곽선
    accentColor: "#1E6E7E", // 괴수 발광 틸 강조
  },
  // ─── 🎀 썰핑크류 세계관 18종 Wave 1(2026-08-20) — 골격·음성 불변, 화풍·말투만 분화 ───
  ssulregress: {
    name: "썰회귀",
    emoji: "🔄",
    barColor: "#E4E0F5",   // 회귀 라벤더 상단바
    borderColor: "#9B8FD4", // 시간 균열 보라 윤곽선
    accentColor: "#4A3E96", // 심연 남보라 강조
  },
  ssulcider: {
    name: "썰사이다",
    emoji: "🥤",
    barColor: "#F6EBD2",   // 샴페인 골드 상단바
    borderColor: "#D4B067", // 골드 윤곽선
    accentColor: "#7A5A18", // 딥 브론즈 골드 강조
  },
  ssulzombie: {
    name: "썰좀비",
    emoji: "🧟",
    barColor: "#DFE5DE",   // 탈색 그레이그린 상단바
    borderColor: "#8FA391", // 이끼 회녹색 윤곽선
    accentColor: "#3C5142", // 짙은 생존 그린 강조
  },
  ssulacademy: {
    name: "썰마법",
    emoji: "🪄",
    barColor: "#E0E6F4",   // 교복 남색 크림 상단바
    borderColor: "#8C9BC8", // 아카데미 블루 윤곽선
    accentColor: "#2E3E7A", // 짙은 남색 강조
  },
  ssulolympus: {
    name: "썰올림포스",
    emoji: "⚡",
    barColor: "#F5EFDF",   // 대리석 아이보리 상단바
    borderColor: "#CDBA8A", // 올리브 골드 윤곽선
    accentColor: "#8A6A2A", // 신전 금색 강조
  },
  ssulhunter: {
    name: "썰헌터",
    emoji: "⚔️",
    barColor: "#EFDCDC",   // 다크레드 크림 상단바
    borderColor: "#B87A7A", // 블러드 로즈 윤곽선
    accentColor: "#8C1F26", // 헌터 다크레드 강조
  },
  ssuloffice: {
    name: "썰오피스",
    emoji: "🏢",
    barColor: "#E3E8ED",   // 오피스 쿨그레이 상단바
    borderColor: "#9BAABB", // 스틸 블루그레이 윤곽선
    accentColor: "#37536E", // 짙은 네이비 블루 강조
  },
  ssuldetect: {
    name: "썰탐정",
    emoji: "🕵️",
    barColor: "#EDE2D2",   // 세피아 크림 상단바
    borderColor: "#BFA482", // 느와르 브라운 윤곽선
    accentColor: "#6B4A28", // 진세피아 강조
  },
  ssuldokkaebi: {
    name: "썰도깨비",
    emoji: "👺",
    barColor: "#F3DFDA",   // 단청 살구 상단바
    borderColor: "#C58A7A", // 민화 적갈 윤곽선
    accentColor: "#A32A22", // 도깨비 적색 강조
  },
  ssulseonhyeop: {
    name: "썰선협",
    emoji: "🧘",
    barColor: "#E1F0EC",   // 옥색 안개 상단바
    borderColor: "#94C2B6", // 비취 윤곽선
    accentColor: "#2E7A68", // 짙은 옥녹색 강조
  },
  ssulmurim: {
    name: "썰무림",
    emoji: "🗡️",
    barColor: "#E7E4DE",   // 화선지 상단바
    borderColor: "#9C948A", // 먹회색 윤곽선
    accentColor: "#C0442A", // 주홍 한 점 강조
  },
  ssulmedieval: {
    name: "썰왕국",
    emoji: "🏰",
    barColor: "#E5E3DE",   // 스톤 그레이 상단바
    borderColor: "#A8A29A", // 성벽 회갈 윤곽선
    accentColor: "#8E2436", // 왕가 진홍 강조
  },
  ssulsaga: {
    name: "썰사가",
    emoji: "⛵",
    barColor: "#DDE7EF",   // 한랭 해무 상단바
    borderColor: "#8CA7BE", // 북해 블루 윤곽선
    accentColor: "#2C5578", // 심해 네이비 강조
  },
  ssulpharaoh: {
    name: "썰파라오",
    emoji: "🏺",
    barColor: "#F2E6CC",   // 샌드골드 상단바
    borderColor: "#CFB077", // 사막 금모래 윤곽선
    accentColor: "#1F4C8F", // 라피스 블루 강조
  },
  ssulvoyage: {
    name: "썰항해",
    emoji: "🚀",
    barColor: "#DCE6F0",   // 우주 새벽 상단바
    borderColor: "#7F9CBC", // 딥네이비 윤곽선
    accentColor: "#10688C", // 시안 홀로그램 강조
  },
  ssulmecha: {
    name: "썰메카",
    emoji: "🤖",
    barColor: "#E2E5E8",   // 스틸그레이 상단바
    borderColor: "#9AA4AE", // 기체 강철 윤곽선
    accentColor: "#C2560F", // 경고등 오렌지 강조
  },
  ssulmaou: {
    name: "썰마왕",
    emoji: "😈",
    barColor: "#E8DFF2",   // 마왕성 라일락 상단바
    borderColor: "#A78CC8", // 퍼플 윤곽선
    accentColor: "#5A2C86", // 마왕 딥퍼플 강조
  },
  ssuldisaster: {
    name: "썰재난",
    emoji: "🚨",
    barColor: "#F4E7D0",   // 비상등 앰버 크림 상단바
    borderColor: "#D0AC6C", // 앰버 윤곽선
    accentColor: "#8A4A12", // 딥 앰버 브라운 강조
  },
  // ─── 🎨 하위 화풍 승격 배치 A 29종(2026-08-21) — 화풍 무드에 맞춘 3색, 기존 명도 체계 준수 ───
  ssulmongle: {
    name: "썰몽글",
    emoji: "🧸",
    barColor: "#FBEEE2",   // 몽글 크림 상단바
    borderColor: "#E0B892", // 포근한 베이지 윤곽선
    accentColor: "#8A5A2B", // 따뜻한 브라운 강조
  },
  ssulchorok: {
    name: "썰초록",
    emoji: "🌿",
    barColor: "#E6F2E2",   // 연둣빛 수채 상단바
    borderColor: "#97C08D", // 이파리 초록 윤곽선
    accentColor: "#35682F", // 짙은 숲 그린 강조
  },
  ssulnemo: {
    name: "썰네모",
    emoji: "⬜",
    barColor: "#ECEFF3",   // 페일 그레이 상단바
    borderColor: "#A9B4C0", // 도형 실루엣 윤곽선
    accentColor: "#3E4A57", // 차분한 슬레이트 강조
  },
  ssulorigi: {
    name: "썰오리기",
    emoji: "✂️",
    barColor: "#F7EEE0",   // 크래프트지 상단바
    borderColor: "#CBAF88", // 종이 결 베이지 윤곽선
    accentColor: "#6E5330", // 짙은 크래프트 브라운 강조
  },
  ssulpopcorn: {
    name: "썰팝콘",
    emoji: "💥",
    barColor: "#FDECD6",   // 팝콘 크림 상단바
    borderColor: "#F0AE55", // 레트로 오렌지 윤곽선
    accentColor: "#9A4A10", // 빈티지 인쇄 브릭 강조
  },
  ssulkkoma: {
    name: "썰꼬마",
    emoji: "🖍️",
    barColor: "#FCE9EF",   // 크레용 핑크 상단바
    borderColor: "#EFA7BE", // 파스텔 로즈 윤곽선
    accentColor: "#A63D62", // 진한 크레용 자주 강조
  },
  ssulyagwang: {
    name: "썰야광",
    emoji: "💠",
    barColor: "#E0F2F4",   // 야광 민트 상단바
    borderColor: "#62BFC9", // 네온 시안 윤곽선
    accentColor: "#10646E", // 심야 딥틸 강조
  },
  ssulminhwa: {
    name: "썰민화",
    emoji: "🐯",
    barColor: "#FBEEDC",   // 민화 장지색 상단바
    borderColor: "#D9A94E", // 전통 황토 윤곽선
    accentColor: "#8C4A1E", // 단청 적갈 강조
  },
  ssulseonbi: {
    name: "썰선비",
    emoji: "🖌️",
    barColor: "#EFEDE6",   // 한지 미색 상단바
    borderColor: "#A8A294", // 먹 번짐 회갈 윤곽선
    accentColor: "#3A382F", // 농묵 먹색 강조
  },
  ssuldaenggi: {
    name: "썰댕기",
    emoji: "🎀",
    barColor: "#FBE6EC",   // 댕기 연분홍 상단바
    borderColor: "#E29AB0", // 다홍 리본 윤곽선
    accentColor: "#93304F", // 진홍 댕기 강조
  },
  ssulhanyang: {
    name: "썰한양",
    emoji: "🏯",
    barColor: "#F5E9DC",   // 기와빛 크림 상단바
    borderColor: "#C99B6E", // 한양 토담 윤곽선
    accentColor: "#7A4520", // 고동 기와 강조
  },
  ssulpungsok: {
    name: "썰풍속",
    emoji: "📜",
    barColor: "#F3EBD8",   // 담채 장지색 상단바
    borderColor: "#C4AE7C", // 풍속화 황갈 윤곽선
    accentColor: "#6B5427", // 먹담채 갈색 강조
  },
  ssulgunggwol: {
    name: "썰궁궐",
    emoji: "🎎",
    barColor: "#E7EFE8",   // 단청 옥색 상단바
    borderColor: "#8FB59A", // 궁중 청록 윤곽선
    accentColor: "#1F5540", // 의궤 심록 강조
  },
  ssulhyeopgaek: {
    name: "썰협객",
    emoji: "⚔️",
    barColor: "#E3E4E6",   // 먹선 은회색 상단바
    borderColor: "#8E9296", // 강철 회색 윤곽선
    accentColor: "#23262A", // 칠흑 먹 강조
  },
  ssulyeonbun: {
    name: "썰연분",
    emoji: "🌸",
    barColor: "#F9E9F2",   // 연분홍 파스텔 상단바
    borderColor: "#DFA7C8", // 살구빛 로즈 윤곽선
    accentColor: "#8A3A6B", // 고운 자주 강조
  },
  ssulseollem: {
    name: "썰설렘",
    emoji: "💓",
    barColor: "#FDE7EA",   // 설렘 코랄 상단바
    borderColor: "#F2A0AC", // 로즈 핑크 윤곽선
    accentColor: "#A62D42", // 딥 체리 강조
  },
  ssulmulbit: {
    name: "썰물빛",
    emoji: "💧",
    barColor: "#E6F1F8",   // 물빛 하늘 상단바
    borderColor: "#92C0DD", // 수채 번짐 블루 윤곽선
    accentColor: "#2C6183", // 깊은 물빛 남색 강조
  },
  ssuldugeun: {
    name: "썰두근",
    emoji: "🎬",
    barColor: "#FBEDE3",   // 극장 앰버 상단바
    borderColor: "#E5AE8C", // 따뜻한 살구 윤곽선
    accentColor: "#96502B", // 시네마 브릭 강조
  },
  ssuljangmi: {
    name: "썰장미",
    emoji: "🌹",
    barColor: "#F6EEDA",   // 아르누보 아이보리 상단바
    borderColor: "#CBAE63", // 장식 골드 윤곽선
    accentColor: "#7A5C14", // 앤티크 골드 강조
  },
  ssulkongdak: {
    name: "썰콩닥",
    emoji: "🧸",
    barColor: "#FDEEE7",   // 콩닥 살구 상단바
    borderColor: "#F4B3A0", // 홍조 코랄 윤곽선
    accentColor: "#A94A32", // 따뜻한 테라코타 강조
  },
  ssulbaram: {
    name: "썰바람",
    emoji: "🌾",
    barColor: "#F1F3E2",   // 들풀 연둣빛 상단바
    borderColor: "#B7C288", // 바람결 올리브 윤곽선
    accentColor: "#56662A", // 깊은 초원 그린 강조
  },
  ssulinhwa: {
    name: "썰인화",
    emoji: "📷",
    barColor: "#F0EBE4",   // 인화지 아이보리 상단바
    borderColor: "#B7A794", // 필름 세피아 윤곽선
    accentColor: "#5C4B3A", // 암실 브라운 강조
  },
  ssulhero: {
    name: "썰히어로",
    emoji: "💥",
    barColor: "#E4EBF7",   // 코믹 스카이 상단바
    borderColor: "#7B9AD6", // 히어로 블루 윤곽선
    accentColor: "#1F3E8C", // 코발트 액션 강조
  },
  ssulnoir: {
    name: "썰느와르",
    emoji: "🎬",
    barColor: "#E6E6E6",   // 느와르 실버 상단바
    borderColor: "#909090", // 모노크롬 그레이 윤곽선
    accentColor: "#1C1C1C", // 칠흑 섀도 강조
  },
  ssulbulkkot: {
    name: "썰불꽃",
    emoji: "🔥",
    barColor: "#FDE8DC",   // 불꽃 살구 상단바
    borderColor: "#F09A63", // 화염 오렌지 윤곽선
    accentColor: "#A83B10", // 타오르는 레드 강조
  },
  ssulgeojang: {
    name: "썰거장",
    emoji: "🖼️",
    barColor: "#F2E8D8",   // 유화 캔버스 상단바
    borderColor: "#C0A472", // 황토 임파스토 윤곽선
    accentColor: "#5F4418", // 고전 앰버 브라운 강조
  },
  ssulmukgaek: {
    name: "썰묵객",
    emoji: "🖌️",
    barColor: "#E9E7E2",   // 한지 담묵 상단바
    borderColor: "#979288", // 붓결 회갈 윤곽선
    accentColor: "#2E2B26", // 농묵 흑 강조
  },
  ssulgangcheol: {
    name: "썰강철",
    emoji: "🧊",
    barColor: "#E4EAEE",   // 강철 아이스 상단바
    borderColor: "#93A6B3", // 메탈 그레이블루 윤곽선
    accentColor: "#2B4453", // 딥 스틸 네이비 강조
  },
  ssuldesaeng: {
    name: "썰데생",
    emoji: "✏️",
    barColor: "#EDECEA",   // 데생지 상단바
    borderColor: "#A3A09B", // 흑연 그레이 윤곽선
    accentColor: "#3C3A36", // 목탄 차콜 강조
  },
  // ─── 🎨 하위 화풍 승격 배치 B 18종(2026-08-21) — 화풍 무드에 맞춘 3색, 기존 명도 체계 준수 ───
  // ── 망령계 4 ──
  ssulyogoe: {
    name: "썰요괴",
    emoji: "🎎",
    barColor: "#F0E6D6",   // 바랜 화지 상단바
    borderColor: "#C2A882", // 목판 안료 윤곽선
    accentColor: "#6B4A2A", // 짙은 먹갈색 강조
  },
  ssulbeonjim: {
    name: "썰번짐",
    emoji: "🩸",
    barColor: "#E4EAEA",   // 젖은 종이 상단바
    borderColor: "#8FA6A6", // 청록 워시 윤곽선
    accentColor: "#7A2A2A", // 핏빛 얼룩 강조
  },
  ssulmoktan: {
    name: "썰목탄",
    emoji: "🖤",
    barColor: "#E9E9E9",   // 모노 화지 상단바
    borderColor: "#9A9A9A", // 흑연 그레이 윤곽선
    accentColor: "#2E2E2E", // 짙은 목탄 강조
  },
  ssulsindang: {
    name: "썰신당",
    emoji: "🔮",
    barColor: "#F3E3EC",   // 무속 연분홍 상단바
    borderColor: "#B98FB0", // 오방 자주 윤곽선
    accentColor: "#6A2A55", // 깊은 자주 강조
  },
  // ── 세오계 7 ──
  ssulppetit: {
    name: "썰쁘띠",
    emoji: "🧸",
    barColor: "#FFEFF3",   // 치비 팝 핑크 상단바
    borderColor: "#F5B3C4", // 파스텔 로즈 윤곽선
    accentColor: "#B03A5C", // 진한 체리 강조
  },
  ssulmanga: {
    // ★리뷰 P3 수리: 종전 무채색 3색이 썰목탄·기존 썰준지와 한 계열로 뭉쳐 액자 구분이 안 됐다.
    //   인쇄지 웜톤 + 잉크블루 강조로 틀어 셋을 분리한다(화풍은 그대로 흑백 만화).
    name: "썰망가",
    emoji: "📗",
    barColor: "#F6F1E6",   // 만화 인쇄지 크림 상단바
    borderColor: "#C4B79A", // 바랜 갱지 베이지 윤곽선
    accentColor: "#26406B", // 잉크 블루 강조
  },
  ssulgongryak: {
    name: "썰공략",
    emoji: "💗",
    barColor: "#FBE9F3",   // 갤겜 UI 핑크 상단바
    borderColor: "#EAA6CC", // 셀 렌더 로즈 윤곽선
    accentColor: "#A32E76", // 딥 마젠타 강조
  },
  ssulsegimal: {
    name: "썰세기말",
    emoji: "📼",
    barColor: "#EFE7DC",   // 바랜 셀 크림 상단바
    borderColor: "#B9A88F", // 필름 그레인 베이지 윤곽선
    accentColor: "#6B563A", // 세피아 브라운 강조
  },
  ssulfigure: {
    name: "썰피규어",
    emoji: "🧊",
    barColor: "#E8F0F6",   // PVC 광택 상단바
    borderColor: "#9DBACE", // 스튜디오 블루그레이 윤곽선
    accentColor: "#2F5A76", // 딥 코발트 강조
  },
  ssultumyeong: {
    name: "썰투명",
    emoji: "🎨",
    barColor: "#EAF4F7",   // 맑은 워시 상단바
    borderColor: "#9CC9D6", // 수채 아쿠아 윤곽선
    accentColor: "#2E6B7C", // 깊은 청록 강조
  },
  ssulsaegyeonpil: {
    name: "썰색연필",
    emoji: "✏️",
    barColor: "#FBF1E4",   // 동화책 크림 상단바
    borderColor: "#DCBE96", // 색연필 살구 윤곽선
    accentColor: "#7E5A2E", // 따뜻한 브라운 강조
  },
  // ── 미래계 7 ──
  ssulcheongryang: {
    name: "썰청량",
    emoji: "✨",
    barColor: "#E7F3FB",   // 청량 스카이 상단바
    borderColor: "#94C4E6", // 셀 애니 블루 윤곽선
    accentColor: "#1F5B8C", // 선명한 딥블루 강조
  },
  ssultoy: {
    name: "썰토이",
    emoji: "🧸",
    barColor: "#FFF1E4",   // 프렌들리 크림 상단바
    borderColor: "#F0B87E", // 3D 렌더 앰버 윤곽선
    accentColor: "#9A5416", // 따뜻한 번트 오렌지 강조
  },
  ssulleijeo: {
    name: "썰레이저",
    emoji: "💥",
    barColor: "#FFF0DC",   // 코믹 인쇄 상단바
    borderColor: "#F5B24A", // 팝 옐로 윤곽선
    accentColor: "#A8300E", // 원색 브릭 레드 강조
  },
  ssuldwitgolmok: {
    name: "썰뒷골목",
    emoji: "🌃",
    barColor: "#E5E6F0",   // 젖은 야경 상단바
    borderColor: "#9096B8", // 네온 반사 인디고 윤곽선
    accentColor: "#3A2A6B", // 깊은 자정 퍼플 강조
  },
  ssulposter: {
    name: "썰포스터",
    emoji: "🎨",
    barColor: "#F1EFE9",   // 포스터 용지 상단바
    borderColor: "#AFAAA0", // 플랫 그레이 윤곽선
    accentColor: "#B33A22", // 그래픽 버밀리언 강조
  },
  ssulmalgeum: {
    name: "썰맑음",
    emoji: "🖌️",
    barColor: "#EEF0FA",   // 아침빛 라벤더 상단바
    borderColor: "#AFB4DE", // 수채 페리윙클 윤곽선
    accentColor: "#3E4490", // 짙은 인디고 강조
  },
  ssulbot: {
    name: "썰봇",
    emoji: "🤖",
    barColor: "#E9F5F1",   // 로봇 민트 상단바
    borderColor: "#97CBBB", // 소프트 민트 윤곽선
    accentColor: "#2A6B5B", // 딥 티얼 강조
  },
  // ─── 📚 범용 썰 장르 스타일 23종(2026-08-21) — 장르 세계관 웨이브 ───
  ssuluimun: {
    name: "썰의문",
    emoji: "🔍",
    barColor: "#E8EDF1",   // 청회색 도해 상단바
    borderColor: "#9FB3C2", // 단면도 청회 윤곽선
    accentColor: "#B14A05", // 도해 주황 강조
  },
  ssulmanyak: {
    name: "썰만약",
    emoji: "⏳",
    barColor: "#F0EAE0",   // 고서 세피아 상단바
    borderColor: "#C2AE93", // 바랜 양피지 윤곽선
    accentColor: "#2A4670", // 갈림길 청색 강조
  },
  ssulseoljeong: {
    name: "썰설정",
    emoji: "📕",
    barColor: "#EDE8F2",   // 설정집 라일락 상단바
    borderColor: "#A99BC4", // 도감 바이올렛 윤곽선
    accentColor: "#4A3577", // 심연 자주 강조
  },
  ssulsinhwa: {
    name: "썰신화",
    emoji: "🏛️",
    barColor: "#E9E9E2",   // 회색조 석회 상단바
    borderColor: "#B3B0A0", // 마모 스톤 윤곽선
    accentColor: "#7A5A12", // 금빛 부조 강조
  },
  ssulsaengjon: {
    name: "썰생존",
    emoji: "🧭",
    barColor: "#E3E9E6",   // 설원 회록 상단바
    borderColor: "#93A8A0", // 혹한 세이지 윤곽선
    accentColor: "#26443C", // 심록 다큐 강조
  },
  ssulscam: {
    name: "썰스캠",
    emoji: "🚨",
    barColor: "#EDE6E6",   // 경보 애시로즈 상단바
    borderColor: "#B08F8F", // 스릴러 더스크 윤곽선
    accentColor: "#9B1B1B", // 경고 레드 강조
  },
  ssulsoreum: {
    name: "썰소름",
    emoji: "😨",
    barColor: "#E8E8EA",   // 무채 저채도 상단바
    borderColor: "#A5A5AC", // 위화감 그레이 윤곽선
    accentColor: "#33333D", // 잉크 차콜 강조
  },
  ssulsimri: {
    name: "썰심리",
    emoji: "🧠",
    barColor: "#F2ECF6",   // 파스텔 라벤더 상단바
    borderColor: "#C3AED6", // 그라디언트 모브 윤곽선
    accentColor: "#5B3D82", // 딥 바이올렛 강조
  },
  ssulsangsik: {
    name: "썰상식",
    emoji: "💡",
    barColor: "#FFF3DC",   // 팝 크림 상단바
    borderColor: "#F0C878", // 인포 앰버 윤곽선
    accentColor: "#96590A", // 번트 앰버 강조
  },
  ssulgwanchal: {
    name: "썰관찰",
    emoji: "👁️",
    barColor: "#ECEEF1",   // 정밀 슬레이트 상단바
    borderColor: "#A8B0BA", // 뉴트럴 슬레이트 윤곽선
    accentColor: "#39414D", // 딥 슬레이트 강조
  },
  ssulbeonyeok: {
    name: "썰번역",
    emoji: "🌐",
    barColor: "#E6F0F6",   // 글로벌 스카이 상단바
    borderColor: "#96BCD4", // 월드 블루 윤곽선
    accentColor: "#1B5175", // 딥 오션 강조
  },
  ssulsiseon: {
    name: "썰세계시선",
    emoji: "🌏",
    barColor: "#F0EEEA",   // 웜그레이 자연광 상단바
    borderColor: "#B5AFA6", // 관찰자 스톤 윤곽선
    accentColor: "#544D43", // 차분한 토프 강조
  },
  ssulgeukhan: {
    name: "썰극한",
    emoji: "🔥",
    barColor: "#F5E9DF",   // 현장 샌드 상단바
    borderColor: "#D69E72", // 불꽃 카퍼 윤곽선
    accentColor: "#8E3400", // 인더스트리 러스트 강조
  },
  ssulheungmang: {
    name: "썰흥망",
    emoji: "📉",
    barColor: "#EBEBE6",   // 잿빛 뉴스프린트 상단바
    borderColor: "#ADADA4", // 탈색 애시 윤곽선
    accentColor: "#5A5730", // 바랜 올리브골드 강조
  },
  ssulgyeongmae: {
    name: "썰경매",
    emoji: "🔨",
    barColor: "#F0E7E9",   // 벨벳 애시로즈 상단바
    borderColor: "#C29AA3", // 경매장 더스티 윤곽선
    accentColor: "#6E1F32", // 딥 버건디 강조
  },
  ssulguchul: {
    name: "썰구출",
    emoji: "🐾",
    barColor: "#EFF3E9",   // 온기 라이트그린 상단바
    borderColor: "#AEC79B", // 수채 리프 윤곽선
    accentColor: "#39592F", // 딥 모스 강조
  },
  ssulretro: {
    name: "썰레트로",
    emoji: "📼",
    barColor: "#F3EDE4",   // 필름 색바램 상단바
    borderColor: "#C9B79E", // 레트로 세피아 윤곽선
    accentColor: "#7A4E1F", // 빈티지 브라운 강조
  },
  ssulbamsayeon: {
    name: "썰밤사연",
    emoji: "🌙",
    barColor: "#E4E9F4",   // 심야 페리윙클 상단바
    borderColor: "#93A3C6", // 부스 인디고 윤곽선
    accentColor: "#233060", // 딥 미드나잇 강조
  },
  ssulfact: {
    name: "썰팩트",
    emoji: "✅",
    barColor: "#EEF5EF",   // 에디토리얼 화이트그린 상단바
    borderColor: "#9CC3A6", // 체크 세이지 윤곽선
    accentColor: "#1B5F35", // 팩트 그린 강조
  },
  ssulgeunal: {
    name: "썰그날",
    emoji: "📸",
    barColor: "#F2ECE4",   // 옛사진 세피아 상단바
    borderColor: "#C6B097", // 인화지 탠 윤곽선
    accentColor: "#66401F", // 현상액 브라운 강조
  },
  ssuljiri: {
    name: "썰지리",
    emoji: "🗺️",
    barColor: "#E7EFEA",   // 지형도 민트그레이 상단바
    borderColor: "#9CBFAA", // 등고선 세이지 윤곽선
    accentColor: "#2A5644", // 지층 딥그린 강조
  },
  // 📍 지역썰(2026-08-26) — 향토 녹색 계열. 이웃한 썰지리(지형도 민트그레이)와 구별되게
  //   차가운 회녹 대신 볕 든 들녘 쪽 따뜻한 연둣빛으로 잡는다.
  ssullocal: {
    name: "지역썰",
    emoji: "📍",
    barColor: "#EAF2E6",   // 볕 든 들녘 연둣빛 상단바
    borderColor: "#A9C79B", // 논두렁 세이지 윤곽선
    accentColor: "#2F5D3A", // 향토 딥그린 강조
  },
  ...REGIONAL_CHANNEL_THEMES,
  ssulsingirok: {
    name: "썰신기록",
    emoji: "🏆",
    barColor: "#F6EFDD",   // 트로피 크림 상단바
    borderColor: "#DCC177", // 골드 리프 윤곽선
    accentColor: "#7D5C05", // 딥 골드 강조
  },
  ssulbihind: {
    name: "썰비하인드",
    emoji: "🎬",
    barColor: "#EAE7EF",   // 백스테이지 페일 상단바
    borderColor: "#A79FBE", // 기자재 실루엣 윤곽선
    accentColor: "#3B3060", // 무대 밖 인디고 강조
  },
  // ─── 🖼️ 썰핑크류 화풍 스타일 20종(2026-08-21) — Wave 2 (마지막 등록 배치) ───
  ssulpopup: {
    name: "썰팝업북",
    emoji: "📗",
    barColor: "#F7F0E6",   // 펼친 지면 크림 상단바
    borderColor: "#D9B3A6", // 파스텔 색지 윤곽선
    accentColor: "#A33A38", // 색지 코랄 강조
  },
  ssuljasu: {
    name: "썰자수",
    emoji: "🪡",
    barColor: "#EFEDE6",   // 무명 리넨 상단바
    borderColor: "#B5B29C", // 박음질 실 윤곽선
    accentColor: "#7A3F46", // 다홍 자수실 강조
  },
  ssulgeurimja: {
    name: "썰그림자",
    emoji: "🫥",
    barColor: "#F3E7CE",   // 호박빛 배경막 상단바
    borderColor: "#C6A263", // 등불 테두리 윤곽선
    accentColor: "#2B2117", // 절지 인형 먹빛 강조
  },
  ssulbeullok: {
    name: "썰블록",
    emoji: "🧩",
    barColor: "#F0F3F8",   // 조립판 화이트 상단바
    borderColor: "#8FA3C0", // 블록 그림자 윤곽선
    accentColor: "#1B4FA8", // 원색 블루 블록 강조
  },
  ssulvoxel: {
    name: "썰복셀",
    emoji: "🟩",
    barColor: "#EAF2E1",   // 잔디 큐브 라이트 상단바
    borderColor: "#8DB273", // 블록 지형 윤곽선
    accentColor: "#2C5A1E", // 심록 큐브 강조
  },
  ssullowpoly: {
    name: "썰로우폴리",
    emoji: "🔷",
    barColor: "#DFEFEC",   // 민트 페일 상단바
    borderColor: "#7FB0A8", // 각진 면 윤곽선
    accentColor: "#10534B", // 딥 틸 강조
  },
  ssulscratch: {
    name: "썰스크래치",
    emoji: "🌒",
    barColor: "#DBE3EA",   // 은청 긁힘 상단바
    borderColor: "#74848F", // 판면 강청 윤곽선
    accentColor: "#0B1B27", // 심야 판면 강조
  },
  ssulbyeokhwa: {
    name: "썰벽화",
    emoji: "🗿",
    barColor: "#F1E8DB",   // 석회 회벽 상단바
    borderColor: "#C7A98C", // 균열 진 회벽 윤곽선
    accentColor: "#8E3A22", // 테라코타 안료 강조
  },
  ssulmosaic: {
    name: "썰모자이크",
    emoji: "🏵️",
    barColor: "#EFEADB",   // 회반죽 줄눈 상단바
    borderColor: "#B79A5C", // 금박 타일 윤곽선
    accentColor: "#1C3A6B", // 심청 유리조각 강조
  },
  ssuldongpan: {
    name: "썰동판",
    emoji: "⚒️",
    barColor: "#F2ECE1",   // 압인 종이 상단바
    borderColor: "#BCA184", // 세피아 잉크 윤곽선
    accentColor: "#4A3318", // 동판 각선 강조
  },
  ssulukiyo: {
    name: "썰우키요",
    emoji: "🌊",
    barColor: "#EFEDE2",   // 화지 크림 상단바
    borderColor: "#96AEBE", // 남색 안료 윤곽선
    accentColor: "#8C2226", // 홍색 안료 강조
  },
  ssulcheongsajin: {
    name: "썰푸른인화",
    emoji: "📘",
    barColor: "#E2EAF3",   // 감광지 페일 상단바
    borderColor: "#7891B2", // 인화 얼룩 윤곽선
    accentColor: "#123458", // 프러시안 블루 강조
  },
  ssulmannyeonpil: {
    name: "썰만년필",
    emoji: "🖋️",
    barColor: "#F2F4F0",   // 스케치북 지면 상단바
    borderColor: "#AFBBAE", // 종이 결 윤곽선
    accentColor: "#2B4A63", // 잉크 남청 강조
  },
  ssulartdeco: {
    name: "썰아르데코",
    emoji: "🥂",
    barColor: "#EDE8DE",   // 샴페인 페일 상단바
    borderColor: "#BFA76F", // 금선 장식 윤곽선
    accentColor: "#0F3A33", // 에메랄드 흑청 강조
  },
  ssuloetgwanggo: {
    name: "썰옛광고",
    emoji: "📻",
    barColor: "#E8F1EA",   // 민트 지면 상단바
    borderColor: "#9CC0AC", // 인쇄 망점 윤곽선
    accentColor: "#A9431F", // 살구 주홍 강조
  },
  ssulrubber: {
    name: "썰러버호스",
    emoji: "🎩",
    barColor: "#F5EEDC",   // 필름 아이보리 상단바
    borderColor: "#B09468", // 비네트 세피아 윤곽선
    accentColor: "#43301A", // 잉크 흑갈 강조
  },
  ssulcollage: {
    name: "썰콜라주",
    emoji: "🗞️",
    barColor: "#F4EFE1",   // 누런 지면 상단바
    borderColor: "#BFAA85", // 찢긴 종이 윤곽선
    accentColor: "#A8232F", // 오려낸 주홍 색면 강조
  },
  ssuly2k: {
    name: "썰Y2K",
    emoji: "💿",
    barColor: "#E6F1F6",   // 아쿠아 글로스 상단바
    borderColor: "#9CBFD1", // 프로스티드 윤곽선
    accentColor: "#3A3C9E", // 일렉트릭 인디고 강조
  },
  ssulcamcorder: {
    name: "썰캠코더",
    emoji: "📹",
    barColor: "#EDE5EC",   // VHS 마젠타 페일 상단바
    borderColor: "#A795A5", // 트래킹 노이즈 윤곽선
    accentColor: "#5A2340", // 자주 색번짐 강조
  },
  ssulpolla: {
    name: "썰폴라",
    emoji: "🏞️",
    barColor: "#F6F2EA",   // 필름 흰 테두리 상단바
    borderColor: "#C4B7A7", // 바랜 인화 윤곽선
    accentColor: "#3F6E6F", // 청록 기운 색 강조
  },
  // ─── 실사 사진 8종(2026-07-08, msg 1180) — 촬영축 4 + 감성축 4 ───
  ssuldocu: {
    name: "썰다큐",
    emoji: "📷",
    barColor: "#E4E7E9",   // 뉴트럴 그레이 상단바
    borderColor: "#A9B2B8", // 다큐 그레이 윤곽선
    accentColor: "#465158", // 차분한 차콜 강조
  },
  ssulcinema: {
    name: "썰시네마",
    emoji: "🎞️",
    barColor: "#DDE4E6",   // 시네마 틸그레이 상단바
    borderColor: "#8FA2A8", // 필름 틸 윤곽선
    accentColor: "#243238", // 시네마 틸차콜 강조
  },
  ssuldaily: {
    name: "썰데일리",
    emoji: "🤳",
    barColor: "#FFE3E0",   // 산뜻 코랄 상단바
    borderColor: "#F7A9A0", // 데일리 코랄 윤곽선
    accentColor: "#E85C50", // 스냅 코랄레드 강조
  },
  ssulmono: {
    name: "썰모노",
    emoji: "🖤",
    barColor: "#E8E8E8",   // 모노 라이트그레이 상단바
    borderColor: "#9A9A9A", // 모노 미드그레이 윤곽선
    accentColor: "#2B2B2B", // 모노 블랙 강조
  },
  ssulleim: {
    name: "썰레임",
    emoji: "💗",
    barColor: "#FDE9F1",   // 뽀샤시 로즈 상단바
    borderColor: "#F6B8D4", // 소프트 핑크 윤곽선
    accentColor: "#E86FA6", // 심쿵 로즈 강조
  },
  ssulgrium: {
    name: "썰그리움",
    emoji: "🍂",
    barColor: "#F4E6CE",   // 골든아워 크림 상단바
    borderColor: "#D8B27E", // 앰버 윤곽선
    accentColor: "#A56A2E", // 노스탤지어 앰버 강조
  },
  ssulsaebyeok: {
    name: "썰새벽",
    emoji: "🌧️",
    barColor: "#DDE6EE",   // 블루아워 상단바
    borderColor: "#93A9C2", // 새벽 블루 윤곽선
    accentColor: "#45607E", // 멜랑콜리 딥블루 강조
  },
  ssulhaesal: {
    name: "썰햇살",
    emoji: "☀️",
    barColor: "#FBF0D8",   // 아침 햇살 크림 상단바
    borderColor: "#EED08A", // 햇살 옐로우 윤곽선
    accentColor: "#C79A3A", // 따뜻한 골드 강조
  },
  ssultem: {
    name: "썰템",
    emoji: "🛍️",
    barColor: "#ECE7DF",   // 프리미엄 스튜디오 아이보리 상단바
    borderColor: "#C9A24B", // 커머셜 골드 윤곽선
    accentColor: "#8A6D1F", // 딥 골드 강조
  },
  ssulshop: {
    name: "썰쇼핑",
    emoji: "🛒",
    barColor: "#3C3C3C",   // 진회색 헤더바(원본 이븐쇼핑, 흰 채널명)
    borderColor: "#2A2A2A", // 헤더/제목 경계 진회색
    accentColor: "#FFFFFF", // 흰색 강조(뒤로가기 화살표·햄버거 톤)
  },
  jachissul: {
    name: "자취썰",
    emoji: "🍳",
    barColor: "#FFF6E9",   // 따뜻한 크림 아이보리 상단바(밝은 자취 주방 톤)
    borderColor: "#F0C36D", // 버터·계란 골드 윤곽선
    accentColor: "#E8792B", // 잘 익은 주황(식욕 강조색)
  },
  ssultech: {
    name: "테크썰",
    emoji: "🔩",
    barColor: "#0F0F0F",   // 다크 헤더(쇼핑 패밀리 톤·#659 배경 복귀 준수)
    borderColor: "#1E1E1E", // 다크 경계
    accentColor: "#3DA9FC", // 일렉트릭 블루(테크 강조 — 골드/민트 계열과 구별)
  },
  honeyitem: {
    name: "꿀템썰",
    emoji: "🍯",
    barColor: "#0F0F0F",   // 다크 헤더(채널 워터마크 톤 — 쇼핑 쇼츠 패밀리①)
    borderColor: "#1E1E1E", // 다크 경계
    accentColor: "#FFC83D", // 꿀 골드(마스터 고유색 — 민트는 변주 갓템썰이 원주인 실측색으로 가져감)
  },
  godtem: {
    name: "갓템썰",
    emoji: "✨",
    barColor: "#0F0F0F",
    borderColor: "#0F0F0F",
    accentColor: "#57E6C4", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  gatseongbi: {
    name: "갓성비썰",
    emoji: "💸",
    barColor: "#1C1C1C",
    borderColor: "#333333",
    accentColor: "#FF5D7A", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  pickshot: {
    name: "픽템썰",
    emoji: "🎯",
    barColor: "#0D0D0D",
    borderColor: "#222222",
    accentColor: "#4DD7FF", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  matfriends: {
    name: "맛템썰",
    emoji: "🍜",
    barColor: "#121212",
    borderColor: "#121212",
    accentColor: "#FF8A3D", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  hamshort: {
    name: "간식썰",
    emoji: "🍢",
    barColor: "#24407E",
    borderColor: "#1B3163",
    accentColor: "#2F80ED", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  jaemishow: {
    name: "잼쇼썰",
    emoji: "🎪",
    barColor: "#8A7168",
    borderColor: "#7A6158",
    accentColor: "#2F6BFF", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  snackpocket: {
    name: "스낵썰",
    emoji: "🍿",
    barColor: "#2B2B2B",
    borderColor: "#1F1F1F",
    accentColor: "#8A8A8A", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  jiwoomung: {
    name: "멍템썰",
    emoji: "🐶",
    barColor: "#FFFFFF",
    borderColor: "#FFFFFF",
    accentColor: "#9A9A9A", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  dalleshop: {
    name: "달래썰",
    emoji: "🪴",
    barColor: "#000000",
    borderColor: "#000000",
    accentColor: "#F5FF3D", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  hyungnim: {
    name: "형님썰",
    emoji: "🤙",
    barColor: "#171717",
    borderColor: "#2A2A2A",
    accentColor: "#F0C243", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  igeone: {
    name: "이거썰",
    emoji: "💡",
    barColor: "#15616D",
    borderColor: "#0F4F5A",
    accentColor: "#999999", // 벤치마크 채널 타이틀 줄2 실측 계열
  },
  giftssul: {
    name: "선물썰",
    emoji: "🎁",
    barColor: "#FFFFFF",
    borderColor: "#FFFFFF",
    accentColor: "#FFFFFF",
  },
  uniqueshop: {
    name: "유니크썰",
    emoji: "📱",
    barColor: "#F1F1F3",
    borderColor: "#E2E2E5",
    accentColor: "#2A2A2A",
  },
  jjinshop: {
    name: "찐템썰",
    emoji: "🎬",
    barColor: "#000000",
    borderColor: "#000000",
    accentColor: "#FFFFFF",
  },
  ssulpizza: {
    name: "썰피자",
    emoji: "🍕",
    barColor: "#26241A",
    borderColor: "#3A3628",
    accentColor: "#E3C15A",
  },
  salimssul: {
    name: "살림썰",
    emoji: "🧺",
    barColor: "#F26D8B",
    borderColor: "#D9576F",
    accentColor: "#FFFFFF",
  },
  musentem: {
    name: "무슨썰",
    emoji: "🥄",
    barColor: "#4FA6DB",
    borderColor: "#3E8FC4",
    accentColor: "#FFFFFF",
  },
  roomfriends: {
    name: "방구석썰",
    emoji: "🛋",
    barColor: "#FFFFFF",
    borderColor: "#EEEEEE",
    accentColor: "#FF7A59",
  },
  // 🏦 KB 다이렉트형(회장님 2026-09-03 · 크랩스 테넌트 전용) — 색은 전부 원본 이미지 픽셀 실측.
  //   ★채널명 하드 충돌 검사 완료(2026-09-03: "KB"·"다이렉트" 포함 기존 채널명 0건).
  //   ★barColor/borderColor 는 실제로 렌더에 들어가지만, name·emoji 는 헤더 이미지 슬롯
  //     (titleImage·emojiImage)이 우선하므로 **이미지를 지웠을 때의 폴백**으로만 쓰인다.
  kbdirect: {
    name: "KB손해보험 | 다이렉트",
    emoji: "🐻",
    barColor: "#F5F5F5",    // 헤더 바 실측(원본 y 0–910 전역 최빈색)
    borderColor: "#776C61", // 헤더 하단 굵은 띠 실측(원본 y 911–955)
    accentColor: "#776C61", // 햄버거 ☰ 색 — 워드마크와 같은 회갈색 계열
  },
  ssulnight: {
    name: "썰한밤",
    emoji: "🌙",
    barColor: "#000000",   // 순검정 캔버스(센지한밤 실측 — 헤더는 워터마크처럼 배경에 녹임)
    borderColor: "#0A0A0A", // 검정 경계(비가시 — 벤치마크는 헤더 없음)
    accentColor: "#F5FF00", // 형광노랑 강조(타이틀 줄1 실측 #F4FF00~#F7FF00 중앙값)
  },
  knowdoc: {
    name: "지식다큐",       // "지식다큐" 미사용 확인됨(충돌 검사 완료, 회장님 2026-07-16)
    emoji: "📜",
    barColor: "#F5F1EA",   // 파스텔 베이지(풀블리드 여백 바탕 — 16:9 렌더러는 헤더바 미렌더, 워터마크만)
    borderColor: "#E3DACB", // 크림 경계
    accentColor: "#3D5A99", // 차분한 블루 강조
  },
  seniorlife: {
    name: "인생사연",
    emoji: "🧓",
    barColor: "#141210",
    borderColor: "#3A322B",
    accentColor: "#FFB84D",
  },
  econstory: {
    name: "머니툰",         // "머니툰" 미사용 확인됨(충돌 검사 완료, 회장님 2026-07-16)
    emoji: "💰",
    barColor: "#16161C",   // 딥 차콜 상단바(16:9 렌더러는 헤더바 미렌더, 워터마크만)
    borderColor: "#2A2A33", // 차콜 경계
    accentColor: "#FFD84D", // 골드 강조(경제 무드)
  },
  infoknow: {
    name: "똑똑백과",       // "똑똑백과" 미사용 확인됨(충돌 검사 완료, 회장님 2026-07-16)
    emoji: "🧠",
    barColor: "#0B0B0E",   // 잉크 블랙 상단바(16:9 렌더러는 헤더바 미렌더, 워터마크만)
    borderColor: "#222533", // 딥 블루그레이 경계
    accentColor: "#4DC3FF", // 시안 강조(지식 무드)
  },
  // ── 🎞 16:9 2차 배치 6종(2026-07-17) — 전부 헤더바 미렌더(워터마크만), 색은 갤러리 카드 브랜딩용 ──
  cosmos: {
    name: "우주극장",
    emoji: "🌌",
    barColor: "#05070F",   // 딥 스페이스 블랙
    borderColor: "#101422",
    accentColor: "#8FC1FF", // 스타 블루
  },
  nightbook: {
    name: "밤의서재",
    emoji: "🌑",
    barColor: "#0A0A0A",   // 목탄 블랙
    borderColor: "#1C1C1C",
    accentColor: "#CBB68C", // 촛불 골드
  },
  darkcase: {
    name: "다크미스터리",
    emoji: "🗃",
    barColor: "#060608",   // 사건 파일 블랙
    borderColor: "#1A1A20",
    accentColor: "#B3261E", // 사건 레드
  },
  famefall: {
    name: "인물흥망사",
    emoji: "⭐",
    barColor: "#0B0A0C",
    borderColor: "#221E24",
    accentColor: "#FFC24D", // 스포트라이트 골드
  },
  sleepdocu: {
    name: "잠드는역사",
    emoji: "🌙",
    barColor: "#17120B",   // 야간 세피아
    borderColor: "#2B2214",
    accentColor: "#C9A96A", // 세피아 골드
  },
  horrorrank: {
    name: "괴담랭킹",
    emoji: "🕯",
    barColor: "#050507",
    borderColor: "#1C1216",
    accentColor: "#FF3B30", // 블러드 레드
  },
  // ── 🎞 16:9 3차 배치 2종(2026-07-18 딥 스카우트) ──
  stickfall: {
    name: "몰락극장",
    emoji: "📉",
    barColor: "#0D0D10",   // 몰락 차콜
    borderColor: "#222228",
    accentColor: "#E5484D", // 몰락 레드
  },
  jinsei: {
    name: "人生物語",       // JP 마켓 — 시장 언어 라벨(カキコ 관례)
    emoji: "🌸",
    barColor: "#16110F",   // 다다미 웜 다크
    borderColor: "#2C221E",
    accentColor: "#E8A7B8", // 사쿠라
  },
  // ── 🎞 16:9 4차 배치 11종(2026-07-18 "풀완비" 딥 스카우트) — 전부 헤더바 미렌더(워터마크만) ──
  gyakuten: {
    name: "逆転物語",       // JP 마켓 — 시장 언어 라벨
    emoji: "💥",
    barColor: "#14100E",   // 웜 다크
    borderColor: "#2A2018",
    accentColor: "#FFB13D", // 앰버
  },
  sengoku: {
    name: "戦国絵巻",       // JP 마켓
    emoji: "🏯",
    barColor: "#0E0A08",   // 흑칠 갑주 다크
    borderColor: "#241812",
    accentColor: "#D4AF37", // 금박
  },
  kaidan: {
    name: "深夜怪談",       // JP 마켓
    emoji: "👹",
    barColor: "#07090D",   // 심야 블루블랙
    borderColor: "#16191F",
    accentColor: "#9BB8D4", // 창백한 청
  },
  teikoku: {
    name: "帝国興亡史",     // JP 마켓
    emoji: "🏰",
    barColor: "#0A0C12",   // 고지도 다크 블루
    borderColor: "#1A1E28",
    accentColor: "#D4AF37", // 제국 금색
  },
  nemureru: {
    name: "眠れる歴史",     // JP 마켓
    emoji: "🌕",
    barColor: "#0A1216",   // 달빛 남색 다크
    borderColor: "#182428",
    accentColor: "#D4C08A", // 달빛 골드
  },
  yadam: {
    name: "야담극장",
    emoji: "🏮",
    barColor: "#120C06",   // 한지 등불 웜 다크
    borderColor: "#281C10",
    accentColor: "#F2A65A", // 등불 주황
  },
  verdict: {
    name: "판결극장",
    emoji: "⚖️",
    barColor: "#0C0E14",   // 법정 잉크 다크
    borderColor: "#1C2028",
    accentColor: "#FFD84D", // 저울 금색
  },
  geomap: {
    name: "세계정세",
    emoji: "🗺",
    barColor: "#0B0E10",   // 위성지도 다크 그레이
    borderColor: "#1A2024",
    accentColor: "#E76F51", // 지도 테라코타
  },
  wisdom: {
    name: "지혜의밤",
    emoji: "🪷",
    barColor: "#0E1410",   // 산사 심록 다크
    borderColor: "#1E2820",
    accentColor: "#A8C3A0", // 세이지 그린
  },
  gunggeum: {
    name: "궁금극장",
    emoji: "❓",
    barColor: "#F2F4F6",   // ★밝은 미니멀 그레이(유일한 라이트 스킨)
    borderColor: "#D8DCE2", // 라이트 카드 디바이더
    accentColor: "#4A90D9", // 도해 블루
  },
  trivia: {
    name: "별별과학",
    emoji: "🧪",
    barColor: "#0D1014",   // 스튜디오 블랙
    borderColor: "#1C222A",
    accentColor: "#FF8C42", // 실험 오렌지
  },
  briefing: {
    name: "브리핑극장",
    emoji: "📊",
    barColor: "#0B1220",   // 브리핑 네이비
    borderColor: "#1C2940",
    accentColor: "#3E8EF7", // 브리핑 블루
  },
  blueprint: {
    name: "기관 브리핑", // PR-3 회장님 결정 — 설계도 1종을 Gov(기관) 가족으로 흡수(id 불변)
    emoji: "📐",
    barColor: "#F4F7FB",   // Gov 라이트 파스텔
    borderColor: "#D9E2F0",
    accentColor: "#3B6FE0",
  },
  blueprintSite: {
    name: "현장 브리핑",
    emoji: "🏗",
    barColor: "#0C1017",
    borderColor: "#232B38",
    accentColor: "#FF9A3D", // 세이프티 오렌지
  },
  blueprintCorp: {
    name: "기업 하이라이트",
    emoji: "🏢",
    barColor: "#0C1020",
    borderColor: "#1D2742",
    accentColor: "#6EE7F9", // 프리미엄 시안
  },
  blueprintLecture: {
    name: "강의 브리핑",
    emoji: "🎓",
    barColor: "#0C1D5F",
    borderColor: "#22317F",
    accentColor: "#2FE0E0", // 학습 민트
  },
  blueprintMotion: {
    name: "모션 브리핑",
    emoji: "🧊",
    barColor: "#EEF0F3",
    borderColor: "#DDE1E7",
    accentColor: "#2F6BFF", // 브랜드색 1개(테넌트 색이 있으면 그것)
  },
  blueprintShowreel: {
    name: SHOWREEL_STYLE_LABEL,
    emoji: "🎬",
    barColor: "#0B0D12",
    borderColor: "#232833",
    accentColor: "#FF5A1F", // 강한 원색 1개(테넌트 색이 있으면 그것)
  },
  blueprintColorCut: {
    name: COLOR_CUT_STYLE_LABEL,
    emoji: "🎨",
    barColor: "#2956FF",
    borderColor: "#1F45D6",
    accentColor: "#2F5BFF", // 브랜드색 1개 — 장면색 4개의 씨앗(테넌트 색이 있으면 그것)
  },
  // ── 🏗 배치5 건설·산업안전 9종(회장님 2026-07-18) — 16:9 3종은 batch5 스펙 brandedHeader 정본 ──
  gongdocu: {
    name: "현장기록",
    emoji: "🏗",
    barColor: "#14100C",   // 현장 새벽 블랙
    borderColor: "#2A2016",
    accentColor: "#FF6A00", // 세이프티 오렌지
  },
  safetoon: {
    name: "안전툰",
    emoji: "⛑",
    barColor: "#1A1408",   // 카툰 딥브라운
    borderColor: "#33290F",
    accentColor: "#FFD400", // 카툰 옐로
  },
  zoosite: {
    name: "동물현장",
    emoji: "🦺",
    barColor: "#0C1410",   // 숲 그린블랙
    borderColor: "#1E2E24",
    accentColor: "#7ED957", // 라임 그린
  },
  // 9:16 숏폼 썰 6종 — 골격(SSULCINE 다크·SEOLEMON 게시판 라이트·HONEYITEM 다크)에 맞춘 채널색.
  achassul: {
    name: "아차사고썰",
    emoji: "🚧",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#FF7A1A", // 세이프티 오렌지 채널명
  },
  banjangssul: {
    name: "김반장썰",
    emoji: "📢",
    barColor: "#F3E4C6",   // 따뜻한 크림 게시판 상단바
    borderColor: "#D9B877", // 탄 골드 윤곽선
    accentColor: "#8A5A1E", // 정 있는 브라운 강조
  },
  sitetoon: {
    name: "현장툰썰",
    emoji: "🔧",
    barColor: "#FBEFA6",   // 세이프티 옐로 게시판 상단바
    borderColor: "#E9CE3A", // 옐로 윤곽선
    accentColor: "#3A2E0A", // 카툰 다크브라운 강조
  },
  zoocrew: {
    name: "동물반장썰",
    emoji: "🐻",
    barColor: "#DEF2DD",   // 프레시 그린 게시판 상단바
    borderColor: "#8FCF8C", // 그린 윤곽선
    accentColor: "#256B2A", // 포레스트 그린 강조
  },
  sagoilji: {
    name: "현장일지썰",
    emoji: "🚨",
    barColor: "#E6E7E9",   // 모노 라이트그레이 상단바(보고서 룩)
    borderColor: "#9AA0A6", // 그레이 윤곽선
    accentColor: "#C62828", // 경보 레드 강조
  },
  safequiz: {
    name: "안전퀴즈썰",
    emoji: "✅",
    barColor: "#0E1A14",   // 다크 헤더(HONEYITEM 골격·ssultech 톤)
    borderColor: "#1E3A2A",
    accentColor: "#22C55E", // 세이프티 그린(정답 강조 — meta 줄2)
  },
  // ── 🏗 배치5b 건설·산업안전 확장 11종(회장님 2026-07-18) — 16:9 5종은 batch5b 스펙 brandedHeader 정본 ──
  courtcase: {
    name: "판례극장",
    emoji: "⚖️",
    barColor: "#120D0B",   // 법정 다크
    borderColor: "#2A1E18",
    accentColor: "#C0392B", // 딥레드(쟁점 badge)
  },
  structlab: {
    name: "구조연구소",
    emoji: "📐",
    barColor: "#0A1220",   // 블루프린트 딥블루
    borderColor: "#16243A",
    accentColor: "#29B6F6", // 시안(원리 badge)
  },
  myungjang: {
    name: "명장록",
    emoji: "🎖",
    barColor: "#14100C",   // 회고 세피아 블랙
    borderColor: "#262019",
    accentColor: "#CD9B62", // 브론즈(구술 인용)
  },
  sagorecord: {
    name: "현장연대기",
    emoji: "🕰",
    barColor: "#100F0D",   // 아카이브 다크
    borderColor: "#232019",
    accentColor: "#FFB300", // 앰버(연도·수치 mega)
  },
  safenomics: {
    name: "안전경제",
    emoji: "💰",
    barColor: "#0B1210",   // 차콜 딥그린
    borderColor: "#1A2A24",
    accentColor: "#00C853", // 머니그린(금액·배수 mega)
  },
  // 🏗 [건설] 사고 사례 브리핑(2026-10-09) — 빨간 «중대재해 사례» 딱지(정답지 Edu-B 회사 틀) · 예방은 노랑(세그 color).
  sagobrief: {
    name: "사고 사례 브리핑",
    emoji: "📋",
    barColor: "#111316",   // 현장 다크
    borderColor: "#2A2D33",
    accentColor: "#E53935", // 레드(사례·개요 딱지)
  },
  // 🌅 [건설] 아침 안전 조회(소장 레인 10-11) — 헤더는 숨김(스튜디오 피부) · 색은 v3 정본(초록 체크).
  sojangMorning: {
    name: "아침 안전 조회",
    emoji: "🌅",
    barColor: "#18202C",   // 정본 글자색(24,32,44)
    borderColor: "#2A3240",
    accentColor: "#249158", // 정본 초록(36,145,88 · 체크)
  },
  // 9:16 숏폼 썰 6종 — 골격(HONEYITEM 다크·SEOLEMON 게시판 라이트·SSULCINE/SSULLETTER 시네마틱)에 맞춘 채널색.
  jeomgumssul: {
    name: "점검썰",
    emoji: "🧯",
    barColor: "#15110A",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#2E2414",
    accentColor: "#FFB300", // 점검 앰버(meta 줄2)
  },
  sinipssul: {
    name: "신입일기썰",
    emoji: "👷",
    barColor: "#FCEFD6",   // 따뜻한 크림 게시판 상단바(SEOLEMON 골격)
    borderColor: "#E7C98C", // 웜 골드 윤곽선
    accentColor: "#C7752A", // 신입 웜브라운 강조
  },
  sangsikssul: {
    name: "안전상식썰",
    emoji: "📏",
    barColor: "#E7F0F7",   // 라이트 블루 게시판 상단바(SEOLEMON 골격)
    borderColor: "#A9C9E0", // 블루 윤곽선
    accentColor: "#1C5C8C", // 상식 딥블루 강조
  },
  yaganssul: {
    name: "야간현장썰",
    emoji: "🌙",
    barColor: "#0B0F1A",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0F1A",
    accentColor: "#4EC5C1", // 야간 청록 채널명
  },
  gujossul: {
    name: "한컷구조썰",
    emoji: "📐",
    barColor: "#E6F0F6",   // 라이트 블루프린트 게시판 상단바(SEOLEMON 트리비아 골격·플랫 도해)
    borderColor: "#A6C6DC", // 블루프린트 시안 윤곽선
    accentColor: "#1E6FA8", // 블루프린트 딥시안 강조
  },
  pyeonjissul: {
    name: "가족편지썰",
    emoji: "💌",
    barColor: "#120E0B",   // 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#120E0B",
    accentColor: "#F0A868", // 편지 노을빛 채널명
  },
  // ── 🛡 보험 테넌트 특화 20종(회장님 2026-07-18 세계관 확장 Step 1) — 16:9 8종은 보험 스펙 brandedHeader 정본 ──
  insucase: {
    name: "보상극장",
    emoji: "⚖️",
    barColor: "#120D0B",   // 조정 회의실 다크
    borderColor: "#2A1E18",
    accentColor: "#C0392B", // 딥레드(쟁점 badge)
  },
  yakgwanlab: {
    name: "약관연구소",
    emoji: "📑",
    barColor: "#0A1220",   // 청사진 딥네이비
    borderColor: "#16243A",
    accentColor: "#29B6F6", // 시안(원리 badge)
  },
  lifeplan: {
    name: "인생보장극장",
    emoji: "👨‍👩‍👧",
    barColor: "#140E08",   // 노을빛 웜블랙
    borderColor: "#2A1D12",
    accentColor: "#FF8C42", // 웜오렌지(생애 이벤트 badge)
  },
  fraudfile: {
    name: "추적파일",
    emoji: "🕵️",
    barColor: "#100D0D",   // 수사 사무실 다크
    borderColor: "#241C1C",
    accentColor: "#FF3B30", // 레드(수법·통계 mega)
  },
  insushow: {
    name: "보험툰극장",
    emoji: "🃏",
    barColor: "#1A1408",   // 카툰 딥브라운(safetoon 구조 복제)
    borderColor: "#33290F",
    accentColor: "#FFD400", // 카툰 옐로(소동 mega)
  },
  fpchronicle: {
    name: "인연록",
    emoji: "🎖",
    barColor: "#14100C",   // 오래된 수첩 세피아 블랙
    borderColor: "#262019",
    accentColor: "#CD9B62", // 브론즈(구술 인용)
  },
  moneyclass: {
    name: "칠판클래스",
    emoji: "🖍",
    barColor: "#0C1016",   // 클린 다크네이비(다크 자막바 유지)
    borderColor: "#1C2430",
    accentColor: "#E53935", // 마커레드(개념 badge)
  },
  insumate: {
    name: "보험메이트",
    emoji: "🤖",
    barColor: "#100C14",   // 파스텔 마스코트 딥퍼플블랙
    borderColor: "#221A2E",
    accentColor: "#B388FF", // 라벤더퍼플(핵심 용어 mega)
  },
  // 9:16 숏폼 썰 12종 — 골격(SSULCINE 다크·SEOLEMON 게시판 라이트·HONEYITEM 다크·SSULLETTER 시네마틱)에 맞춘 채널색.
  bosangssul: {
    name: "실화썰",
    emoji: "💬",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#46C4A8", // 안도 민트그린 채널명
  },
  fpssul: {
    name: "김설계사썰",
    emoji: "📢",
    barColor: "#F3E9D6",   // 따뜻한 크림 게시판 상단바(SEOLEMON 골격)
    borderColor: "#D9BE86", // 웜 골드 윤곽선
    accentColor: "#8A5A1E", // 정 있는 브라운 강조
  },
  bojangquiz: {
    name: "보장퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 퀴즈 블루(정답 강조 — meta 줄2)
  },
  fraudssul: {
    name: "기록썰",
    emoji: "🚨",
    barColor: "#E6E7E9",   // 모노 라이트그레이 상단바(보고서 룩·SEOLEMON 흑백 골격)
    borderColor: "#9AA0A6", // 그레이 윤곽선
    accentColor: "#C62828", // 경보 레드 강조
  },
  yakgwanssul: {
    name: "한줄썰",
    emoji: "📑",
    barColor: "#E7F2F7",   // 라이트 시안 게시판 상단바(SEOLEMON 트리비아 골격)
    borderColor: "#A9CADD", // 시안 윤곽선
    accentColor: "#1E7BA8", // 약관 딥시안 강조
  },
  gajokssul: {
    name: "가족일러썰",
    emoji: "👶",
    barColor: "#FDEFE4",   // 따뜻한 크림 게시판 상단바(SEOLEMON 수채 골격)
    borderColor: "#F0C9A8", // 웜 피치 윤곽선
    accentColor: "#E07A3C", // 가족 웜오렌지 강조
  },
  pixelssul: {
    name: "픽셀썰",
    emoji: "🎮",
    barColor: "#1B1830",   // 딥 아케이드 인디고 상단바(SEOLEMON 게시판 골격·게임 화면)
    borderColor: "#4B3FA0", // 네온 퍼플 윤곽선
    accentColor: "#3DE0B4", // 네온 민트 채널명
  },
  clayssul: {
    name: "클레이썰",
    emoji: "🧸",
    barColor: "#FDEAD8",   // 말랑 크림 게시판 상단바(SEOLEMON 일러 골격)
    borderColor: "#F0BFA0", // 클레이 피치 윤곽선
    accentColor: "#C4622E", // 테라코타 클레이 강조
  },
  promisessul: {
    name: "남긴약속썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C12",
    accentColor: "#E5A9C0", // 약속 로즈 채널명
  },
  webtoonssul: {
    name: "웹툰썰",
    emoji: "🖼",
    barColor: "#EAF0FB",   // 라이트 웹툰 상단바(SEOLEMON 일러 골격)
    borderColor: "#B9C9E8", // 소프트 블루 윤곽선
    accentColor: "#3A63C0", // 웹툰 인디고 강조
  },
  cheonggussul: {
    name: "꿀팁썰",
    emoji: "🧾",
    barColor: "#14140A",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#2E2E14",
    accentColor: "#FFC53D", // 청구 앰버(핵심 강조 — meta 줄2)
  },
  insutoon: {
    name: "보험툰썰",
    emoji: "🃏",
    barColor: "#FBEFA6",   // 세이프티 옐로 게시판 상단바(SEOLEMON 카툰 골격)
    borderColor: "#E9CE3A", // 옐로 윤곽선
    accentColor: "#3A2E0A", // 카툰 다크브라운 강조
  },
  // ── 📈 증권 테넌트 특화 20종(회장님 2026-07-19 세계관 확장 Step 2) — 16:9 8종은 증권 스펙 brandedHeader 정본 ──
  marketbrief: {
    name: "마켓브리핑",
    emoji: "📊",
    barColor: "#081018",   // 방송 스튜디오 딥네이비블랙
    borderColor: "#14243A",
    accentColor: "#2E7DF6", // 마켓블루(국면 badge)
  },
  analystnote: {
    name: "전문가노트",
    emoji: "🎙",
    barColor: "#0B100C",   // 서재 스튜디오 딥그린블랙
    borderColor: "#1A2A1E",
    accentColor: "#1B8A5A", // 딥그린(국면 badge)
  },
  mindtheater: {
    name: "투자심리극장",
    emoji: "🧠",
    barColor: "#0E0A14",   // 심리 드라마 딥퍼플블랙
    borderColor: "#201736",
    accentColor: "#8E6CEF", // 퍼플(국면 badge)
  },
  finhistory: {
    name: "시장의역사",
    emoji: "🏛",
    barColor: "#100F0C",   // 아카이브 세피아 블랙
    borderColor: "#232016",
    accentColor: "#FFB300", // 앰버(연도·수치 mega)
  },
  chartlab: {
    name: "차트연구소",
    emoji: "📈",
    barColor: "#060C14",   // 트레이딩 대시보드 딥네이비
    borderColor: "#102030",
    accentColor: "#38E58C", // 네온그린(원리 badge)
  },
  virtualdesk: {
    name: "버추얼데스크",
    emoji: "🤖",
    barColor: "#0A0D18",   // 미래적 뉴스 데스크 딥블루
    borderColor: "#182238",
    accentColor: "#5AA9FF", // 홀로블루(핵심 키워드 mega)
  },
  antshow: {
    name: "개미극장",
    emoji: "🃏",
    barColor: "#1A1408",   // 카툰 딥브라운(safetoon 구조 복제)
    borderColor: "#33290F",
    accentColor: "#FFD400", // 카툰 옐로(소동 mega)
  },
  moneypress: {
    name: "머니신문",
    emoji: "📰",
    barColor: "#141109",   // 빈티지 신문 잉크 웜블랙
    borderColor: "#2A2416",
    accentColor: "#D64545", // 프레스레드(국면 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SSULCINE 다크·SEOLEMON 게시판·HONEYITEM 다크·SSULLETTER 시네마틱)에 맞춘 채널색.
  tojassul: {
    name: "투자실화썰",
    emoji: "💬",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#4DA3FF", // 투자 시그널블루 채널명
  },
  pbssul: {
    name: "김PB썰",
    emoji: "📢",
    barColor: "#F3E9D6",   // 따뜻한 크림 게시판 상단바(SEOLEMON 골격)
    borderColor: "#D9BE86", // 웜 골드 윤곽선
    accentColor: "#1C5D8C", // PB 신뢰 딥블루 강조
  },
  tojaquiz: {
    name: "투자퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 퀴즈 블루(정답 강조 — meta 줄2)
  },
  docussul: {
    name: "일지썰",
    emoji: "🚨",
    barColor: "#E6E7E9",   // 모노 라이트그레이 상단바(기록 룩·SEOLEMON 흑백 골격)
    borderColor: "#9AA0A6", // 그레이 윤곽선
    accentColor: "#C62828", // 경보 레드 강조
  },
  checkssul: {
    name: "체크썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#2BB673", // 체크 그린(핵심 강조 — meta 줄2)
  },
  miraessul: {
    name: "미래편지썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C12",
    accentColor: "#E5B96A", // 편지 골드 노을 채널명
  },
  anttoon: {
    name: "개미툰썰",
    emoji: "🃏",
    barColor: "#FBEFA6",   // 옐로 게시판 상단바(SEOLEMON 카툰 골격)
    borderColor: "#E9CE3A", // 옐로 윤곽선
    accentColor: "#3A2E0A", // 카툰 다크브라운 강조
  },
  moneypixel: {
    name: "머니픽셀썰",
    emoji: "🎮",
    barColor: "#1B1830",   // 딥 아케이드 인디고 상단바(SEOLEMON 게시판 골격·게임 화면)
    borderColor: "#4B3FA0", // 네온 퍼플 윤곽선
    accentColor: "#3DE0B4", // 네온 민트 채널명
  },
  origamissul: {
    name: "종이접기썰",
    emoji: "🪙",
    barColor: "#F3EEE2",   // 파스텔 종이 크림 상단바(SEOLEMON 일러 골격)
    borderColor: "#D9C9A8", // 종이 베이지 윤곽선
    accentColor: "#C8A24B", // 종이 골드(동전) 강조
  },
  antwebtoon: {
    name: "개미웹툰썰",
    emoji: "🖼",
    barColor: "#EAF0FB",   // 라이트 웹툰 상단바(SEOLEMON 일러 골격)
    borderColor: "#B9C9E8", // 소프트 블루 윤곽선
    accentColor: "#3A63C0", // 웹툰 인디고 강조
  },
  chartssul: {
    name: "한컷차트썰",
    emoji: "📊",
    barColor: "#E7F2F7",   // 라이트 시안 게시판 상단바(SEOLEMON 트리비아 골격·플랫)
    borderColor: "#A9CADD", // 시안 윤곽선
    accentColor: "#1E7BA8", // 차트 딥시안 강조
  },
  minicityssul: {
    name: "미니시티썰",
    emoji: "🏙",
    barColor: "#EAF3EC",   // 파스텔 민트 게시판 상단바(SEOLEMON 일러 골격·미니어처)
    borderColor: "#BFE0C8", // 소프트 그린 윤곽선
    accentColor: "#3E8E6A", // 미니시티 그린 강조
  },
  // ── 🏛 공공기관 테넌트 특화 20종(회장님 2026-07-20 세계관 확장 Step 3) — 16:9 8종은 공공 스펙 brandedHeader 정본 ──
  policybrief: {
    name: "혜택브리핑",
    emoji: "🎁",
    barColor: "#0A1220",   // 공공 브리핑 딥네이비
    borderColor: "#16243A",
    accentColor: "#3B82F6", // 시빅블루(안내·대상·방법 badge)
  },
  towntalk: {
    name: "타운톡",
    emoji: "🎙",
    barColor: "#141008",   // 토크쇼 웜다크
    borderColor: "#2A1E0E",
    accentColor: "#F59E0B", // 웜오렌지(질문·핵심·정리 badge)
  },
  localdocu: {
    name: "우리고장극장",
    emoji: "🌄",
    barColor: "#12100A",   // 브랜딩 다큐 세피아 다크
    borderColor: "#241E12",
    accentColor: "#F2B705", // 선셋골드(구술 quote)
  },
  psatheater: {
    name: "공익극장",
    emoji: "🎬",
    barColor: "#100C0C",   // 캠페인 재연 다크
    borderColor: "#241818",
    accentColor: "#DC2626", // 캠페인레드(주의·확인·기억 badge)
  },
  townmate: {
    name: "동네지킴이",
    emoji: "🐥",
    barColor: "#141206",   // 마스코트 웜다크
    borderColor: "#2A2410",
    accentColor: "#FFD23F", // 병아리옐로(핵심 키워드 mega)
  },
  steplab: {
    name: "절차연구소",
    emoji: "📑",
    barColor: "#0A1218",   // 인포그래픽 딥네이비
    borderColor: "#16283A",
    accentColor: "#10B981", // 민트(단계·준비물·주의 badge)
  },
  signageloop: {
    name: "전광판극장",
    emoji: "🔇",
    barColor: "#060C12",   // 전광판 딥네이비
    borderColor: "#102030",
    accentColor: "#40C4FF", // 라이트블루(안내·기간·문의 badge)
  },
  towncartoon: {
    name: "동네만평",
    emoji: "🖋",
    barColor: "#14120E",   // 만평 잉크 웜블랙
    borderColor: "#2A241A",
    accentColor: "#C2410C", // 잉크레드(풍자·현실·한마디 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SEOLEMON 게시판·HONEYITEM 다크·SSULCINE·SSULLETTER)에 맞춘 채널색.
  simunssul: {
    name: "시민문답썰",
    emoji: "💬",
    barColor: "#E8F1FB",   // 밝은 시빅 게시판 상단바(SEOLEMON 골격)
    borderColor: "#B7CFEA", // 소프트 블루 윤곽선
    accentColor: "#2E6FB5", // 시민문답 딥블루 강조
  },
  jumugwanssul: {
    name: "주무관썰",
    emoji: "📢",
    barColor: "#F1EAD8",   // 따뜻한 크림 창구 상단바(SEOLEMON 골격)
    borderColor: "#D6C08C", // 웜 골드 윤곽선
    accentColor: "#2C6E7A", // 주무관 신뢰 딥틸 강조
  },
  townquizssul: {
    name: "동네퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 퀴즈 블루(정답 강조 — meta 줄2)
  },
  annaessul: {
    name: "안내썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#2BB673", // 안내 그린(핵심 강조 — meta 줄2)
  },
  psassul: {
    name: "공익재연썰",
    emoji: "🚨",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#E5533C", // 캠페인 레드오렌지 채널명
  },
  gohyangssul: {
    name: "고향편지썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C12",
    accentColor: "#E5B96A", // 편지 골드 노을 채널명
  },
  dongnematssul: {
    name: "동네발견썰",
    emoji: "👣",
    barColor: "#100B08",   // 시네마틱 웜다크 헤더(SSULCINE 골격)
    borderColor: "#100B08",
    accentColor: "#E8843C", // 맛집 웜오렌지(김 오르는 따뜻함) 채널명
  },
  townwebtoonssul: {
    name: "동네웹툰썰",
    emoji: "🖼",
    barColor: "#EAF0FB",   // 라이트 웹툰 상단바(SEOLEMON 일러 골격)
    borderColor: "#B9C9E8", // 소프트 블루 윤곽선
    accentColor: "#3A63C0", // 웹툰 인디고 강조
  },
  gayossul: {
    name: "동네가요썰",
    emoji: "🎵",
    barColor: "#FBEEDA",   // 정겨운 웜크림 게시판 상단바(SEOLEMON 골격)
    borderColor: "#E7CB93", // 웜 골드 윤곽선
    accentColor: "#C8562E", // 잔치 웜테라코타 강조
  },
  dohaessul: {
    name: "한컷도해썰",
    emoji: "📊",
    barColor: "#E7F2F7",   // 라이트 시안 게시판 상단바(SEOLEMON 트리비아 골격·플랫)
    borderColor: "#A9CADD", // 시안 윤곽선
    accentColor: "#1E7BA8", // 도해 딥시안 강조
  },
  feltssul: {
    name: "인형극썰",
    emoji: "🧶",
    barColor: "#FBEDE0",   // 포근한 파스텔 크림 상단바(SEOLEMON 일러 골격)
    borderColor: "#E9CDB0", // 양모 베이지 윤곽선
    accentColor: "#C77A4D", // 인형극 웜테라코타 강조
  },
  seasonssul: {
    name: "사계절썰",
    emoji: "🌸",
    barColor: "#FCEAF1",   // 파스텔 벚꽃 상단바(SEOLEMON 수채 일러 골격)
    borderColor: "#EEC6D5", // 블라썸 핑크 윤곽선
    accentColor: "#C86E96", // 수채 블라썸 강조
  },
  // ── 🧒 유아·청소년 교육 테넌트 특화 20종(회장님 2026-07-20 세계관 확장 Step 4) — 16:9 8종은 교육 스펙 brandedHeader 정본 ──
  parenttalk: {
    name: "학부모상담소",
    emoji: "🎓",
    barColor: "#0F0C14",   // 상담 스튜디오 딥퍼플다크
    borderColor: "#241C30",
    accentColor: "#7C5CDB", // 딥퍼플(고민·원인·오늘부터 badge)
  },
  classlive: {
    name: "맛보기교실",
    emoji: "📚",
    barColor: "#081210",   // 강의실 딥틸다크
    borderColor: "#12261F",
    accentColor: "#14B8A6", // 틸(질문·핵심·기억 badge)
  },
  growthdocu: {
    name: "성장극장",
    emoji: "🌱",
    barColor: "#0E120C",   // 성장 다큐 그린블랙
    borderColor: "#1E2A18",
    accentColor: "#7BC96F", // 새싹그린(구술 quote)
  },
  fiveguide: {
    name: "오분가이드",
    emoji: "🧭",
    barColor: "#120E08",   // 실무 가이드 웜다크
    borderColor: "#241A0E",
    accentColor: "#F97316", // 오렌지(준비·단계·완료 badge)
  },
  edumate: {
    name: "공부친구",
    emoji: "🐰",
    barColor: "#140A10",   // 마스코트 핑크다크
    borderColor: "#2A1620",
    accentColor: "#FF6FA5", // 캔디핑크(핵심 키워드 mega)
  },
  conceptlab: {
    name: "개념연구소",
    emoji: "💡",
    barColor: "#0A0E14",   // 개념 도해 딥네이비
    borderColor: "#16243A",
    accentColor: "#3B82F6", // 블루(왜·원리·예시 badge)
  },
  studytoon: {
    name: "학습만화극장",
    emoji: "📖",
    barColor: "#14100A",   // 학습만화 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFC53D", // 코믹옐로(핵심 키워드 mega)
  },
  storyart: {
    name: "동화극장",
    emoji: "🎨",
    barColor: "#100E0A",   // 그림책 낭독 골드블랙
    borderColor: "#221E14",
    accentColor: "#F2C879", // 별빛골드(구술 quote)
  },
  // 9:16 숏폼 썰 12종 — 골격(SEOLEMON 게시판·HONEYITEM 다크·SSULCINE·SSULLETTER)에 맞춘 채널색.
  gominssul: {
    name: "고민썰",
    emoji: "💬",
    barColor: "#EFEAF6",   // 소프트 라벤더 게시판 상단바(SEOLEMON 골격)
    borderColor: "#CFC2E4", // 라벤더 윤곽선
    accentColor: "#7C5CDB", // 상담 딥퍼플 강조
  },
  teacherssul: {
    name: "선생님썰",
    emoji: "📢",
    barColor: "#FBF0DC",   // 따뜻한 크림 교탁 상단바(SEOLEMON 골격)
    borderColor: "#E6CF9E", // 웜 골드 윤곽선
    accentColor: "#2C6E7A", // 선생님 신뢰 딥틸 강조
  },
  eduquizssul: {
    name: "교실퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 퀴즈 블루(정답 강조 — meta 줄2)
  },
  studytipssul: {
    name: "학습꿀팁썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#2BB673", // 체크 그린(핵심 강조 — meta 줄2)
  },
  eungwonssul: {
    name: "응원편지썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C12",
    accentColor: "#E5B96A", // 편지 골드 노을 채널명
  },
  schooldayssul: {
    name: "아이시선썰",
    emoji: "🎒",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#6EC6FF", // 아침 하늘 스카이블루 채널명
  },
  matessul: {
    name: "공부친구썰",
    emoji: "🐰",
    barColor: "#FDE7F0",   // 캔디핑크 게시판 상단바(SEOLEMON 일러 골격)
    borderColor: "#F4B6D0", // 캔디핑크 윤곽선
    accentColor: "#FF6FA5", // 마스코트 캔디핑크 강조
  },
  edutoonssul: {
    name: "학습툰썰",
    emoji: "📖",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 학습만화 앰버 강조
  },
  edupixelssul: {
    name: "픽셀교실썰",
    emoji: "🎮",
    barColor: "#1B1830",   // 딥 아케이드 인디고 상단바(SEOLEMON 게시판 골격·게임 화면)
    borderColor: "#4B3FA0", // 네온 퍼플 윤곽선
    accentColor: "#3DE0B4", // 네온 민트 채널명
  },
  gaenyeomssul: {
    name: "한컷개념썰",
    emoji: "📊",
    barColor: "#E7F2F7",   // 라이트 시안 게시판 상단바(SEOLEMON 트리비아 골격·플랫)
    borderColor: "#A9CADD", // 시안 윤곽선
    accentColor: "#1E7BA8", // 도해 딥시안 강조
  },
  dollssul: {
    name: "인형놀이썰",
    emoji: "🧸",
    barColor: "#FBEDE0",   // 포근한 파스텔 크림 상단바(SEOLEMON 일러 골격)
    borderColor: "#E9CDB0", // 양모 베이지 윤곽선
    accentColor: "#C77A4D", // 인형 웜테라코타 강조
  },
  donghwassul: {
    name: "동화썰",
    emoji: "🌈",
    barColor: "#FBEFF6",   // 파스텔 라일락 상단바(SEOLEMON 수채 일러 골격)
    borderColor: "#EAD0E4", // 소프트 라일락 윤곽선
    accentColor: "#B678B0", // 수채 라일락 강조
  },
  // ── 🛍 커머스 테넌트 특화 20종(회장님 2026-07-20 재설계 v2 "리뷰 연출") — 16:9 8종은 커머스 재설계 스펙 brandedHeader 정본 ──
  luxereview: {
    name: "럭셔리쇼룸",
    emoji: "✨",
    barColor: "#0C0A06",   // 럭셔리 쇼룸 딥블랙
    borderColor: "#241E10",
    accentColor: "#D4AF6A", // 샴페인골드(디테일·소재·무드 badge)
  },
  studioreview: {
    name: "스튜디오리뷰",
    emoji: "🎬",
    barColor: "#07090D",   // 프로덕트 필름 스튜디오 딥블루블랙
    borderColor: "#161B26",
    accentColor: "#4C7DFF", // 일렉트릭블루(컷·디테일·한 줄 평 badge)
  },
  homereview: {
    name: "홈리뷰",
    emoji: "🏠",
    barColor: "#120E0A",   // 홈 라이프스타일 웜다크
    borderColor: "#261E16",
    accentColor: "#E07A5F", // 웜테라코타(일상·좋은 점·아쉬운 점 badge)
  },
  duotalk: {
    name: "듀오리뷰",
    emoji: "🗣",
    barColor: "#0B0D0A",   // 캐주얼 토크 스튜디오 다크
    borderColor: "#1B2318",
    accentColor: "#A3E635", // 라임(주장·반박·결론 badge)
  },
  brandstory: {
    name: "브랜드연대기",
    emoji: "📜",
    barColor: "#12100C",   // 브랜드 아카이브 브론즈블랙
    borderColor: "#241E14",
    accentColor: "#CD9B62", // 브론즈(구술 quote)
  },
  productlab: {
    name: "상품연구소",
    emoji: "🔬",
    barColor: "#0D0A14",   // 성분·원리 도해 딥퍼플다크
    borderColor: "#221A34",
    accentColor: "#8B5CF6", // 퍼플(재료·원리·체크 badge)
  },
  shoptoon: {
    name: "마트툰극장",
    emoji: "🛍",
    barColor: "#1A1408",   // 장보기 카툰 시트콤 웜다크
    borderColor: "#2E2410",
    accentColor: "#FFD400", // 카툰옐로(핵심 키워드 mega)
  },
  minimart: {
    name: "미니어처마켓",
    emoji: "🏪",
    barColor: "#121008",   // 미니어처 디오라마 웜다크
    borderColor: "#241E12",
    accentColor: "#FB7185", // 코랄(오늘의 코너·관찰·한 줄 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SSULCINE·SEOLEMON 게시판·HONEYITEM 다크·SSULLETTER)에 맞춘 채널색.
  jangbossul: {
    name: "장바구니썰",
    emoji: "🧺",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#5FD08A", // 장바구니 프레시그린 채널명
  },
  mdssul: {
    name: "김MD썰",
    emoji: "📢",
    barColor: "#FBF3E4",   // 따뜻한 크림 매대 상단바(SEOLEMON 훈화 골격)
    borderColor: "#E6D3A8", // 웜 골드 윤곽선
    accentColor: "#2C6E7A", // MD 신뢰 딥틸 강조
  },
  pickquizssul: {
    name: "골라보기썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 퀴즈 블루(정답 강조 — meta 줄2)
  },
  sotipssul: {
    name: "쇼핑꿀팁썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#2BB673", // 체크 그린(핵심 강조 — meta 줄2)
  },
  unboxssul: {
    name: "언박싱썰",
    emoji: "📦",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#F5A623", // 개봉 설렘 앰버 채널명
  },
  soljikssul: {
    name: "솔직썰",
    emoji: "🤳",
    barColor: "#FBF1E8",   // 셀피 웜 크림 상단바(SEOLEMON 훈화 골격)
    borderColor: "#E9CDB8", // 웜 베이지 윤곽선
    accentColor: "#E2603C", // 솔직 코랄 강조
  },
  beforessul: {
    name: "비포애프터썰",
    emoji: "🔄",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#F59E0B", // 변화 앰버(after 강조 — meta 줄2)
  },
  dangolssul: {
    name: "단골썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 감성 골격)
    borderColor: "#100C12",
    accentColor: "#E5B96A", // 단골 노을 골드 채널명
  },
  shoptoonssul: {
    name: "마트툰썰",
    emoji: "🛍",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  sangpumssul: {
    name: "한컷상품썰",
    emoji: "📊",
    barColor: "#E7F2F7",   // 라이트 시안 게시판 상단바(SEOLEMON 트리비아 골격·플랫)
    borderColor: "#A9CADD", // 시안 윤곽선
    accentColor: "#1E7BA8", // 도해 딥시안 강조
  },
  minimartssul: {
    name: "미니마켓썰",
    emoji: "🏪",
    barColor: "#FBEDE0",   // 포근한 파스텔 크림 상단바(SEOLEMON 일러 골격)
    borderColor: "#E9CDB0", // 웜 베이지 윤곽선
    accentColor: "#C77A4D", // 미니어처 웜테라코타 강조
  },
  martwebtoonssul: {
    name: "마트웹툰썰",
    emoji: "🖼",
    barColor: "#FBEFF6",   // 파스텔 라일락 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#EAD0E4", // 소프트 라일락 윤곽선
    accentColor: "#B678B0", // 웹툰 라일락 강조
  },
  // ── 🏪 소상공인·자영업 테넌트 특화 20종(회장님 2026-07-20 세계관 확장 Step 6) — 16:9 8종은 소상공인 스펙 brandedHeader 정본 ──
  dalindocu: {
    name: "달인극장",
    emoji: "🔥",
    barColor: "#120C06",   // 손기술 몰입 다큐 웜차콜
    borderColor: "#241808",
    accentColor: "#F59E0B", // 앰버(손끝·리듬·완성 badge)
  },
  sajangdocu: {
    name: "사장의하루",
    emoji: "🌅",
    barColor: "#070D14",   // 새벽~마감 명암 새벽다크
    borderColor: "#14202E",
    accentColor: "#38BDF8", // 새벽블루(새벽·한낮·마감 badge)
  },
  golmokdocu: {
    name: "골목연대기",
    emoji: "🏮",
    barColor: "#140F0A",   // 노포 아카이브 홍등블랙
    borderColor: "#262014",
    accentColor: "#E8A65D", // 홍등앰버(구술 quote)
  },
  jangsaclass: {
    name: "장사교실",
    emoji: "📊",
    barColor: "#071210",   // 데이터 강의 딥틸다크
    borderColor: "#14261F",
    accentColor: "#14B8A6", // 틸(기준·숫자·체크 badge)
  },
  munjeondocu: {
    name: "문전성시극장",
    emoji: "🏃",
    barColor: "#140708",   // 현장 러시 무드 레드다크
    borderColor: "#26141A",
    accentColor: "#F43F5E", // 레드코랄(오픈·러시·마감 badge)
  },
  sigdocu: {
    name: "시그니처극장",
    emoji: "🍽",
    barColor: "#12100A",   // 시네마틱 B롤 골드다크
    borderColor: "#242012",
    accentColor: "#EAB308", // 골드(재료·디테일·완성 badge)
  },
  jangsalab: {
    name: "장사연구소",
    emoji: "🧮",
    barColor: "#0A0A16",   // 상권·마진 도해 인디고다크
    borderColor: "#1A1A2E",
    accentColor: "#6366F1", // 인디고(원리·숫자·오늘 badge)
  },
  golmoktoon: {
    name: "골목툰극장",
    emoji: "🏘",
    barColor: "#180E06",   // 자영업 카툰 시트콤 웜다크
    borderColor: "#2E1C0E",
    accentColor: "#FF8A00", // 카툰오렌지(핵심 키워드 mega)
  },
  // 9:16 숏폼 썰 12종 — 골격(SSULCINE·HONEYITEM 다크·SEOLEMON 게시판·SSULLETTER)에 맞춘 채널색.
  dalinssul: {
    name: "달인썰",
    emoji: "🔥",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#F5B841", // 손기술 앰버 채널명
  },
  sajangssul: {
    name: "사장썰",
    emoji: "🌅",
    barColor: "#0A0D14",   // 푸른 새벽 다크 헤더(SSULCINE 골격)
    borderColor: "#0A0D14",
    accentColor: "#6EA8FF", // 새벽빛 블루 채널명
  },
  openjunbissul: {
    name: "빈가게썰",
    emoji: "🪑",
    barColor: "#0B0B0F",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B0F",
    accentColor: "#E0B060", // 이른 아침 고요한 골드 채널명
  },
  changupssul: {
    name: "체크썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#2BB673", // 체크 그린(핵심 강조 — meta 줄2)
  },
  jangsaquizssul: {
    name: "장사퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 퀴즈 블루(정답 강조 — meta 줄2)
  },
  sonnimssul: {
    name: "목격썰",
    emoji: "👀",
    barColor: "#FFF0E4",   // 따뜻한 크림 상단바(SEOLEMON 골격)
    borderColor: "#F1C9A0", // 웜 오렌지 윤곽선
    accentColor: "#E07B39", // 정 오렌지 강조
  },
  jeongssul: {
    name: "정썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 감성 골격)
    borderColor: "#100C12",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  sajangtokssul: {
    name: "사장솔직썰",
    emoji: "🤳",
    barColor: "#FBF3E4",   // 따뜻한 크림 상단바(SEOLEMON 훈화 골격)
    borderColor: "#E6D3A8", // 웜 골드 윤곽선
    accentColor: "#2C6E7A", // 솔직 신뢰 딥틸 강조
  },
  golmoktoonssul: {
    name: "골목툰썰",
    emoji: "🏘",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  jangsacutssul: {
    name: "한컷장사썰",
    emoji: "📐",
    barColor: "#E9EAFB",   // 라이트 인디고 게시판 상단바(SEOLEMON 트리비아 골격·플랫)
    borderColor: "#BFC2EA", // 인디고 윤곽선
    accentColor: "#4F46E5", // 도해 딥인디고 강조
  },
  gagessul: {
    name: "가게일러썰",
    emoji: "🎨",
    barColor: "#F3EFE6",   // 포근한 파스텔 크림 상단바(SEOLEMON 일러 골격·수채)
    borderColor: "#D9CBB0", // 웜 베이지 윤곽선
    accentColor: "#6B8E6B", // 수채 세이지 강조
  },
  jangsawebtoonssul: {
    name: "장사웹툰썰",
    emoji: "🖼",
    barColor: "#FBEFF6",   // 파스텔 라일락 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#EAD0E4", // 소프트 라일락 윤곽선
    accentColor: "#B678B0", // 웹툰 라일락 강조
  },
  // ── ⚖️ 전문직 테넌트 특화 20종(회장님 2026-07-20 세계관 확장 Step 8) — 16:9 8종은 전문직 스펙 brandedHeader 정본 ──
  officedocu: {
    name: "사무소의하루",
    emoji: "💼",
    barColor: "#080B12",   // 전문가 일상 브이로그 잉크다크
    borderColor: "#141A26",
    accentColor: "#4F7CFF", // 코발트(출근·서류·퇴근 badge)
  },
  counselroom: {
    name: "상담실극장",
    emoji: "🗣",
    barColor: "#071013",   // 상담실 딥틸다크
    borderColor: "#12222B",
    accentColor: "#0EA5E9", // 스카이(사연·쟁점·답변 badge)
  },
  casedrama: {
    name: "각색극장",
    emoji: "🎭",
    barColor: "#120809",   // 재연 드라마 버건디다크
    borderColor: "#241016",
    accentColor: "#C4506B", // 버건디(사연·전개·교훈 badge)
  },
  caserecord: {
    name: "사건기록실",
    emoji: "🗂",
    barColor: "#0B0E12",   // 기록 아카이브 절제 다크
    borderColor: "#1A2029",
    accentColor: "#9DB2CE", // 스틸블루그레이(구술 quote)
  },
  calendarbrief: {
    name: "실무달력",
    emoji: "🗓",
    barColor: "#100C06",   // 시즌 브리핑 앰버다크
    borderColor: "#221A0E",
    accentColor: "#D97706", // 앰버(이번 달·숫자·체크 badge)
  },
  boardlab: {
    name: "화이트보드연구소",
    emoji: "🖍",
    barColor: "#071009",   // 손그림 도해 그린다크
    borderColor: "#122218",
    accentColor: "#10B981", // 그린(개념·도해·정리 badge)
  },
  officetoon: {
    name: "사무소툰극장",
    emoji: "🖊",
    barColor: "#16110A",   // 사무소 카툰 시트콤 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFC53D", // 옐로(핵심 키워드 mega)
  },
  papertheater: {
    name: "페이퍼극장",
    emoji: "📄",
    barColor: "#0C0A12",   // 페이퍼컷 레이어 라벤더다크
    borderColor: "#181226",
    accentColor: "#A78BFA", // 라벤더(개념·절차·한 줄 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SSULCINE·HONEYITEM 다크·SEOLEMON 게시판·SSULLETTER)에 맞춘 채널색.
  officessul: {
    name: "사무소썰",
    emoji: "💼",
    barColor: "#0B0B12",   // 시네마틱 다크 헤더(SSULCINE 골격)
    borderColor: "#0B0B12",
    accentColor: "#6E8CFF", // 코발트 채널명
  },
  counselssul: {
    name: "상담사연썰",
    emoji: "🗣",
    barColor: "#F0F6FA",   // 라이트 스카이 게시판 상단바(SEOLEMON 골격)
    borderColor: "#BCD9E8", // 스카이 윤곽선
    accentColor: "#0284C7", // 상담 딥스카이 강조
  },
  gakssaekssul: {
    name: "각색썰",
    emoji: "🎭",
    barColor: "#120A0C",   // 재연 시네마틱 버건디다크(SSULCINE 골격)
    borderColor: "#120A0C",
    accentColor: "#D06A85", // 재연 버건디 채널명
  },
  pangyeolssul: {
    name: "기록썰",
    emoji: "🗂",
    barColor: "#EEF1F5",   // 라이트 스틸 게시판 상단바(SEOLEMON 골격)
    borderColor: "#C3CDDA", // 스틸 윤곽선
    accentColor: "#4A6178", // 절제 스틸블루 강조
  },
  silmussul: {
    name: "실무꿀팁썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#2BB673", // 체크 그린(핵심 강조 — meta 줄2)
  },
  proquizssul: {
    name: "전문퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 퀴즈 블루(정답 강조 — meta 줄2)
  },
  uiroessul: {
    name: "부치는편지썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 감성 골격)
    borderColor: "#100C12",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  protokssul: {
    name: "전문가솔직썰",
    emoji: "🤳",
    barColor: "#FBF3E4",   // 따뜻한 크림 상단바(SEOLEMON 훈화 골격)
    borderColor: "#E6D3A8", // 웜 골드 윤곽선
    accentColor: "#2C6E7A", // 솔직 신뢰 딥틸 강조
  },
  boardssul: {
    name: "화이트보드썰",
    emoji: "🖍",
    barColor: "#EAF6EF",   // 라이트 그린 게시판 상단바(SEOLEMON 골격·손그림)
    borderColor: "#BEE3CD", // 그린 윤곽선
    accentColor: "#0E9F6E", // 도해 딥그린 강조
  },
  officetoonssul: {
    name: "사무소툰썰",
    emoji: "🖊",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  paperssul: {
    name: "페이퍼썰",
    emoji: "📄",
    barColor: "#F1EEFB",   // 파스텔 라벤더 상단바(SEOLEMON 일러 골격·페이퍼컷)
    borderColor: "#D3C9EE", // 라벤더 윤곽선
    accentColor: "#7C5FD6", // 페이퍼 딥라벤더 강조
  },
  prowebtoonssul: {
    name: "사무소웹툰썰",
    emoji: "🖼",
    barColor: "#FBEFF6",   // 파스텔 라일락 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#EAD0E4", // 소프트 라일락 윤곽선
    accentColor: "#B678B0", // 웹툰 라일락 강조
  },
  // ── 🏠 부동산 테넌트 특화 20종(회장님 2026-07-21 세계관 확장 Step 9) — 16:9 8종은 부동산 스펙 brandedHeader 정본 ──
  jipdeuridocu: {
    name: "집들이극장",
    emoji: "🏡",
    barColor: "#120E0A",   // 룸투어 초대 웜다크
    borderColor: "#241C12",
    accentColor: "#FF8A65", // 웜코랄(현관·거실·뷰 badge)
  },
  imjangdocu: {
    name: "임장극장",
    emoji: "🚶",
    barColor: "#071009",   // 동네 도보 브이로그 그린다크
    borderColor: "#12261A",
    accentColor: "#34D399", // 트레일그린(도착·걷기·총평 badge)
  },
  gyeyakclass: {
    name: "계약서교실",
    emoji: "🛡",
    barColor: "#080C14",   // 소비자 보호 교실 블루다크
    borderColor: "#142036",
    accentColor: "#3B82F6", // 블루(서류·확인·체크 badge)
  },
  showroomdocu: {
    name: "쇼룸극장",
    emoji: "🛋",
    barColor: "#0C0B09",   // 하이엔드 쇼룸 잉크다크
    borderColor: "#201C14",
    accentColor: "#C9A96A", // 골드베이지(빛·소재·무드 badge)
  },
  makeoverdocu: {
    name: "집단장극장",
    emoji: "🔄",
    barColor: "#0E0812",   // 비포애프터 전환 퍼플다크
    borderColor: "#1E1226",
    accentColor: "#A855F7", // 퍼플(비포·과정·애프터 badge)
  },
  siselab: {
    name: "시세연구소",
    emoji: "📈",
    barColor: "#0A0A14",   // 데이터 도해 인디고다크
    borderColor: "#16162A",
    accentColor: "#6D7CFF", // 인디고(데이터·흐름·체크 badge)
  },
  dongnetoon: {
    name: "동네툰극장",
    emoji: "🏘",
    barColor: "#16110A",   // 이사 카툰 시트콤 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFB300", // 옐로(핵심 키워드 mega)
  },
  minitown: {
    name: "미니타운극장",
    emoji: "🏙",
    barColor: "#071013",   // 아이소메트릭 동네 모형 시안다크
    borderColor: "#122229",
    accentColor: "#22D3EE", // 시안(입지·동선·한 줄 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SSULCINE·HONEYITEM 다크·SEOLEMON 게시판·SSULLETTER)에 맞춘 채널색.
  jipdeurissul: {
    name: "초대썰",
    emoji: "🏡",
    barColor: "#100C08",   // 룸투어 시네마틱 웜다크(SSULCINE 골격)
    borderColor: "#100C08",
    accentColor: "#FFA477", // 초대 웜코랄 채널명
  },
  imjangssul: {
    name: "발품썰",
    emoji: "👟",
    barColor: "#0A120C",   // 도보 시네마틱 그린다크(SSULCINE 골격)
    borderColor: "#0A120C",
    accentColor: "#4ADEA0", // 트레일그린 채널명
  },
  sagiyebangssul: {
    name: "확인썰",
    emoji: "🚨",
    barColor: "#1A0E0E",   // 단호 경고 다크 헤더(HONEYITEM 골격)
    borderColor: "#3A1E1E",
    accentColor: "#FF5A52", // 경고 레드(위험 신호 강조 — meta 줄2)
  },
  gyeyakssul: {
    name: "옆자리썰",
    emoji: "📝",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#4C9AFF", // 다정 상담 블루(체크 강조 — meta 줄2)
  },
  jungaetokssul: {
    name: "중개사솔직썰",
    emoji: "🤳",
    barColor: "#FBF3E4",   // 따뜻한 크림 상단바(SEOLEMON 훈화 골격)
    borderColor: "#E6D3A8", // 웜 골드 윤곽선
    accentColor: "#2C6E7A", // 솔직 신뢰 딥틸 강조
  },
  ipjussul: {
    name: "집편지썰",
    emoji: "💌",
    barColor: "#100C12",   // 시네마틱 다크 헤더(SSULLETTER 감성 골격)
    borderColor: "#100C12",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  jibdanjangssul: {
    name: "전후썰",
    emoji: "🔄",
    barColor: "#120A16",   // 전환 시네마틱 퍼플다크(SSULCINE 골격)
    borderColor: "#120A16",
    accentColor: "#C07BF0", // 전환 퍼플 채널명
  },
  sisecutssul: {
    name: "한컷정리썰",
    emoji: "📊",
    barColor: "#EDEEFB",   // 라이트 인디고 게시판 상단바(SEOLEMON 골격·도해)
    borderColor: "#C7CBEF", // 인디고 윤곽선
    accentColor: "#4B54C6", // 데이터 딥인디고 강조
  },
  minitownssul: {
    name: "미니타운썰",
    emoji: "🏙",
    barColor: "#E7F8FB",   // 라이트 시안 게시판 상단바(SEOLEMON 골격·아이소메트릭)
    borderColor: "#BEE7EF", // 시안 윤곽선
    accentColor: "#0E8FA6", // 미니어처 딥시안 강조
  },
  dongnetoonssul: {
    name: "동네툰썰",
    emoji: "🏘",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격·카툰)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  jibwebtoonssul: {
    name: "집웹툰썰",
    emoji: "🖼",
    barColor: "#FBEFF6",   // 파스텔 라일락 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#EAD0E4", // 소프트 라일락 윤곽선
    accentColor: "#B678B0", // 웹툰 라일락 강조
  },
  dongnessul: {
    name: "동네수채썰",
    emoji: "🎨",
    barColor: "#EAF4EC",   // 파스텔 그린 상단바(SEOLEMON 일러 골격·수채)
    borderColor: "#CBE3D0", // 소프트 세이지 윤곽선
    accentColor: "#5B9E7A", // 수채 세이지그린 강조
  },
  // ── 🎓 입시교육 테넌트 특화 20종(회장님 2026-07-21 세계관 확장 Step 10) — 16:9 8종은 입시 스펙 brandedHeader 정본 ──
  myeonggangdocu: {
    name: "명강극장",
    emoji: "🎤",
    barColor: "#120607",   // 강의실 무대 크림슨다크
    borderColor: "#241012",
    accentColor: "#EF4444", // 크림슨(몰입·정곡·어록 badge)
  },
  dokseodocu: {
    name: "독서실극장",
    emoji: "📚",
    barColor: "#090C12",   // 독서실 야간 몰입 잉크다크
    borderColor: "#141B26",
    accentColor: "#A5C8FF", // 미드나잇스카이(구술 quote)
  },
  jeonlyakbrief: {
    name: "전략브리핑",
    emoji: "📊",
    barColor: "#06110C",   // 데이터 권위 딥그린다크
    borderColor: "#0E2419",
    accentColor: "#059669", // 딥그린(흐름·데이터·기준 badge)
  },
  suheomdocu: {
    name: "수험일기극장",
    emoji: "🌅",
    barColor: "#0C0A12",   // 수험 브이로그 라일락다크
    borderColor: "#181226",
    accentColor: "#9F7AEA", // 라일락(아침·교실·밤 badge)
  },
  hapgyeokdocu: {
    name: "합격순간극장",
    emoji: "🎓",
    barColor: "#12100A",   // 합격 클라이맥스 골드다크
    borderColor: "#242012",
    accentColor: "#F5B301", // 골드(디데이·순간·함께 badge)
  },
  plannerlab: {
    name: "플래너극장",
    emoji: "✍️",
    barColor: "#140A0E",   // 스터디 플래너 로즈다크
    borderColor: "#26141A",
    accentColor: "#FB7185", // 로즈(계획·체크·오늘 badge)
  },
  gyosiltoon: {
    name: "교실툰극장",
    emoji: "🏫",
    barColor: "#16110A",   // 교실 카툰 시트콤 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFD43B", // 옐로(핵심 키워드 mega)
  },
  pixelstudy: {
    name: "공부픽셀극장",
    emoji: "🕹",
    barColor: "#071009",   // 도트 게임 퀘스트 그린다크
    borderColor: "#122415",
    accentColor: "#4ADE80", // 네온그린(퀘스트·경험치·레벨업 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SEOLEMON 훈화·SSULCINE 시네마틱·HONEYITEM 다크·SSULLETTER 감성)에 맞춘 채널색.
  myeonggangssul: {
    name: "명강썰",
    emoji: "🎤",
    barColor: "#FBECEC",   // 따뜻한 크림핑크 상단바(SEOLEMON 훈화 골격)
    borderColor: "#F0C9C9", // 크림슨 윤곽선
    accentColor: "#D64545", // 명강 크림슨 딥 강조
  },
  dokseossul: {
    name: "정물썰",
    emoji: "📚",
    barColor: "#0A0D14",   // 야간 몰입 시네마틱 잉크다크(SSULCINE 골격)
    borderColor: "#0A0D14",
    accentColor: "#7FA8E8", // 미드나잇 블루 채널명
  },
  jeonlyakssul: {
    name: "전략썰",
    emoji: "📊",
    barColor: "#0A140E",   // 전략 다크 헤더(HONEYITEM 골격)
    borderColor: "#17281C",
    accentColor: "#10B981", // 전략 그린 강조(체크 — meta 줄2)
  },
  ipsiquizssul: {
    name: "입시퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#F5B301", // 정답 골드 강조(퀴즈 정답 — meta 줄2)
  },
  suheomssul: {
    name: "수험썰",
    emoji: "🌅",
    barColor: "#0C0A14",   // 청춘 시네마틱 라일락다크(SSULCINE 골격)
    borderColor: "#0C0A14",
    accentColor: "#B79AF0", // 라일락 채널명
  },
  hapgyeokssul: {
    name: "그순간썰",
    emoji: "✨",
    barColor: "#12100A",   // 합격 시네마틱 골드다크(SSULCINE 골격)
    borderColor: "#12100A",
    accentColor: "#F5B301", // 합격 골드 채널명
  },
  baekilssul: {
    name: "나에게편지썰",
    emoji: "💌",
    barColor: "#100C10",   // 감성 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C10",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  gangsatokssul: {
    name: "강사솔직썰",
    emoji: "🤳",
    barColor: "#FBF0EC",   // 따뜻한 크림 상단바(SEOLEMON 훈화 골격)
    borderColor: "#F0D3C4", // 웜 테라코타 윤곽선
    accentColor: "#C25A3A", // 솔직 테라코타 강조
  },
  yajassul: {
    name: "지켜보는썰",
    emoji: "🌙",
    barColor: "#0A0C16",   // 밤 공부 시네마틱 블루다크(SSULCINE 골격)
    borderColor: "#0A0C16",
    accentColor: "#6E8BD0", // 야간 블루 채널명
  },
  plannerssul: {
    name: "플래너썰",
    emoji: "✍️",
    barColor: "#FDEEF0",   // 파스텔 로즈 상단바(SEOLEMON 일러 골격·문구)
    borderColor: "#F4CDD4", // 소프트 로즈 윤곽선
    accentColor: "#E24B6A", // 문구 로즈 강조
  },
  gyosiltoonssul: {
    name: "교실툰썰",
    emoji: "🏫",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격·카툰)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  gongbupixelssul: {
    name: "공부픽셀썰",
    emoji: "🕹",
    barColor: "#E7F8EC",   // 연그린 게시판 상단바(SEOLEMON 게시판 골격·픽셀)
    borderColor: "#BEEFC9", // 소프트 그린 윤곽선
    accentColor: "#16A34A", // 픽셀 네온그린 딥 강조
  },
  // ── 🌏 외국인교육 테넌트 특화 20종(회장님 2026-07-21 세계관 확장 Step 11) — 16:9 8종은 외국인교육 스펙 brandedHeader 정본 ──
  hangeulclass: {
    name: "한글교실극장",
    emoji: "📖",
    barColor: "#140808",   // 어학 교실 소프트레드다크
    borderColor: "#261212",
    accentColor: "#F87171", // 소프트레드(표현·예문·따라하기 badge)
  },
  campusdocu: {
    name: "캠퍼스극장",
    emoji: "🎒",
    barColor: "#080C14",   // 유학 브이로그 캠퍼스블루다크
    borderColor: "#142036",
    accentColor: "#60A5FA", // 캠퍼스블루(도착·캠퍼스·저녁 badge)
  },
  culturebridge: {
    name: "컬처브릿지극장",
    emoji: "🎧",
    barColor: "#120612",   // K-culture 학습 마젠타다크
    borderColor: "#241224",
    accentColor: "#E879F9", // 마젠타(장면·표현·내 것 badge)
  },
  interviewdocu: {
    name: "거리인터뷰극장",
    emoji: "🎙",
    barColor: "#120C06",   // 거리 인터뷰 선셋오렌지다크
    borderColor: "#241A0E",
    accentColor: "#FF9F43", // 선셋오렌지(질문·관점·한 마디 badge)
  },
  welcomedocu: {
    name: "환대극장",
    emoji: "🤝",
    barColor: "#06110E",   // 환대 감성 다큐 티일다크
    borderColor: "#0E241F",
    accentColor: "#2DD4BF", // 티일(첫날·손길·미소 badge)
  },
  settlelab: {
    name: "정착연구소",
    emoji: "📑",
    barColor: "#0A0A14",   // 절차 인포그래픽 인디고다크
    borderColor: "#16162A",
    accentColor: "#818CF8", // 인디고(서류·절차·체크 badge)
  },
  hangeultoon: {
    name: "한글툰극장",
    emoji: "🖌",
    barColor: "#16110A",   // 적응기 카툰 시트콤 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFD93D", // 옐로(핵심 키워드 mega)
  },
  scrapbooklab: {
    name: "스크랩북극장",
    emoji: "📔",
    barColor: "#140A0E",   // 한국 발견 콜라주 핑크다크
    borderColor: "#26141C",
    accentColor: "#F472B6", // 핑크(발견·조각·기록 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SEOLEMON 훈화·SSULCINE 시네마틱·HONEYITEM 다크·SSULLETTER 감성)에 맞춘 채널색.
  hangeulssul: {
    name: "한글썰",
    emoji: "📖",
    barColor: "#FBECEC",   // 따뜻한 크림레드 상단바(SEOLEMON 훈화 골격)
    borderColor: "#F0C9C9", // 소프트레드 윤곽선
    accentColor: "#D64545", // 한글 소프트레드 딥 강조
  },
  yuhaksaengssul: {
    name: "유학생썰",
    emoji: "🎒",
    barColor: "#0A0E16",   // 유학 시네마틱 캠퍼스블루다크(SSULCINE 골격)
    borderColor: "#0A0E16",
    accentColor: "#7FA8E8", // 캠퍼스 블루 채널명
  },
  seoulssul: {
    name: "발견썰",
    emoji: "🌃",
    barColor: "#0A0C16",   // 도시 야경 시네마틱 블루다크(SSULCINE 골격)
    borderColor: "#0A0C16",
    accentColor: "#6E8BD0", // 도시 야경 블루 채널명
  },
  culturessul: {
    name: "컬처썰",
    emoji: "🎧",
    barColor: "#FBEAFB",   // 따뜻한 크림마젠타 상단바(SEOLEMON 훈화 골격)
    borderColor: "#F0C9F0", // 마젠타 윤곽선
    accentColor: "#C13BC1", // 컬처 마젠타 딥 강조
  },
  jeongchakssul: {
    name: "정착꿀팁썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#818CF8", // 정착 인디고 강조(체크 — meta 줄2)
  },
  kquizssul: {
    name: "한국퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#F5B301", // 정답 골드 강조(퀴즈 정답 — meta 줄2)
  },
  penpalssul: {
    name: "펜팔썰",
    emoji: "💌",
    barColor: "#100C10",   // 감성 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C10",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  interviewssul: {
    name: "인터뷰썰",
    emoji: "🎙",
    barColor: "#FCF0E8",   // 따뜻한 크림 상단바(SEOLEMON 훈화 골격)
    borderColor: "#F0D3C4", // 선셋오렌지 윤곽선
    accentColor: "#E67E22", // 인터뷰 선셋오렌지 강조
  },
  hangeultoonssul: {
    name: "한글툰썰",
    emoji: "🖌",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격·카툰)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  scrapssul: {
    name: "스크랩썰",
    emoji: "📔",
    barColor: "#FDEAF2",   // 파스텔 핑크 상단바(SEOLEMON 일러 골격·콜라주)
    borderColor: "#F4C9DC", // 소프트 핑크 윤곽선
    accentColor: "#E24B8A", // 콜라주 핑크 강조
  },
  yuhakwebtoonssul: {
    name: "유학웹툰썰",
    emoji: "🖼",
    barColor: "#E9F0FB",   // 연블루 게시판 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#C9DAF0", // 소프트 블루 윤곽선
    accentColor: "#4A78D0", // 웹툰 블루 딥 강조
  },
  hanpyossul: {
    name: "한컷표현썰",
    emoji: "📇",
    barColor: "#E9EAFB",   // 연인디고 게시판 상단바(SEOLEMON 게시판 골격·플랫)
    borderColor: "#C9CDF0", // 소프트 인디고 윤곽선
    accentColor: "#6366F1", // 플랫 인디고 딥 강조
  },
  // ── ⚕️ 병의원 테넌트 특화 20종(회장님 2026-07-21 세계관 확장 Step 12) — 16:9 8종은 병의원 스펙 brandedHeader 정본 ──
  jinryodocu: {
    name: "진료실극장",
    emoji: "🩺",
    barColor: "#070D12",   // 진료실 해설 메디다크
    borderColor: "#122230",
    accentColor: "#4CA7F0", // 메디블루(증상·원리·관리 badge)
  },
  yebangclass: {
    name: "건강습관교실",
    emoji: "🌿",
    barColor: "#071008",   // 생활 예방 그린다크
    borderColor: "#122414",
    accentColor: "#66BB6A", // 그린(습관·오늘·체크 badge)
  },
  byeongwondocu: {
    name: "병원의하루",
    emoji: "🏥",
    barColor: "#0C0A12",   // 의료진 브이로그 바이올렛다크
    borderColor: "#181226",
    accentColor: "#B39DDB", // 소프트바이올렛(아침·회진·마감 badge)
  },
  annaedocu: {
    name: "첫진료안내극장",
    emoji: "🧭",
    barColor: "#070F13",   // 절차 안내 스카이다크
    borderColor: "#122229",
    accentColor: "#4FC3F7", // 스카이(접수·진행·안내 badge)
  },
  bodylab: {
    name: "인체연구소",
    emoji: "🫀",
    barColor: "#140808",   // 인체 인포그래픽 코랄다크
    borderColor: "#261212",
    accentColor: "#FF6B6B", // 코랄레드(구조·원리·관리 badge)
  },
  clayclinic: {
    name: "클레이클리닉극장",
    emoji: "🧸",
    barColor: "#120E0A",   // 클레이 미니 병원 웜다크
    borderColor: "#241C12",
    accentColor: "#FFAB91", // 피치(진찰·처방·한 줄 badge)
  },
  byeongwontoon: {
    name: "병원툰극장",
    emoji: "😷",
    barColor: "#16110A",   // 병원 카툰 시트콤 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFCA28", // 옐로(핵심 키워드 mega)
  },
  ansimart: {
    name: "안심일러극장",
    emoji: "🎨",
    barColor: "#0C100A",   // 수채 안심 세이지다크
    borderColor: "#182414",
    accentColor: "#C5E1A5", // 세이지그린(quote-only 인용)
  },
  // 9:16 숏폼 썰 12종 — 골격(SEOLEMON 훈화·SSULCINE 시네마틱·HONEYITEM 다크·SSULLETTER 감성)에 맞춘 채널색.
  jinryossul: {
    name: "진료실썰",
    emoji: "🩺",
    barColor: "#ECF4FB",   // 따뜻한 크림블루 상단바(SEOLEMON 훈화 골격)
    borderColor: "#C9E0F5", // 메디블루 윤곽선
    accentColor: "#2E7FC4", // 진료 메디블루 딥 강조
  },
  seupgwanssul: {
    name: "습관썰",
    emoji: "🌿",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#66BB6A", // 습관 그린 강조(체크 — meta 줄2)
  },
  geonganquizssul: {
    name: "건강퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#F5B301", // 정답 골드 강조(퀴즈 정답 — meta 줄2)
  },
  byeongwonssul: {
    name: "병원썰",
    emoji: "🏥",
    barColor: "#0B0A12",   // 의료진 시네마틱 바이올렛다크(SSULCINE 골격)
    borderColor: "#0B0A12",
    accentColor: "#9E88CC", // 소프트바이올렛 채널명
  },
  wonjangtokssul: {
    name: "원장솔직썰",
    emoji: "🤳",
    barColor: "#ECF4FB",   // 따뜻한 크림블루 상단바(SEOLEMON 훈화 골격)
    borderColor: "#C9E0F5", // 메디블루 윤곽선
    accentColor: "#2E7FC4", // 원장 메디블루 딥 강조
  },
  kwaeyussul: {
    name: "나직편지썰",
    emoji: "💌",
    barColor: "#100C10",   // 감성 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C10",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  bodycutssul: {
    name: "한컷원리썰",
    emoji: "📐",
    barColor: "#FBECEC",   // 파스텔 코랄 상단바(SEOLEMON 일러 골격·도해)
    borderColor: "#F4C9C9", // 소프트 코랄 윤곽선
    accentColor: "#E24B4B", // 도해 코랄 딥 강조
  },
  yebangcutssul: {
    name: "한컷예방썰",
    emoji: "📇",
    barColor: "#EAF6EC",   // 연그린 게시판 상단바(SEOLEMON 게시판 골격·플랫)
    borderColor: "#C9EFD1", // 소프트 그린 윤곽선
    accentColor: "#2E9E4F", // 플랫 그린 딥 강조
  },
  clayclinicssul: {
    name: "클레이클리닉썰",
    emoji: "🧸",
    barColor: "#FDEFE6",   // 파스텔 피치 상단바(SEOLEMON 일러 골격·클레이)
    borderColor: "#F4D6C4", // 소프트 피치 윤곽선
    accentColor: "#E2814B", // 클레이 피치 딥 강조
  },
  byeongwontoonssul: {
    name: "병원툰썰",
    emoji: "😷",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격·카툰)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  ansimssul: {
    name: "안심일러썰",
    emoji: "🎨",
    barColor: "#EEF6E4",   // 파스텔 세이지 상단바(SEOLEMON 일러 골격·수채)
    borderColor: "#D3E9BC", // 소프트 세이지 윤곽선
    accentColor: "#6F9E3E", // 수채 세이지 딥 강조
  },
  mediwebtoonssul: {
    name: "메디웹툰썰",
    emoji: "🖼",
    barColor: "#E9F0FB",   // 연블루 게시판 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#C9DAF0", // 소프트 블루 윤곽선
    accentColor: "#4A78D0", // 웹툰 블루 딥 강조
  },
  // ── 🚗 자동차 테넌트 특화 20종(회장님 2026-07-21 세계관 확장 Step 13) — 16:9 8종은 자동차 스펙 brandedHeader 정본 ──
  siseungdocu: {
    name: "시승검증극장",
    emoji: "🚗",
    barColor: "#080A12",   // 냉정 검증 시승 건메탈다크
    borderColor: "#141A2A",
    accentColor: "#5C7CFA", // 건메탈블루(첫인상·검증·결론 badge)
  },
  jeongbidocu: {
    name: "정비공방극장",
    emoji: "🔧",
    barColor: "#120C06",   // 수리 과정 공개 오렌지다크
    borderColor: "#241A0E",
    accentColor: "#FF922B", // 오렌지(진단·과정·완료 badge)
  },
  detaildocu: {
    name: "광택극장",
    emoji: "✨",
    barColor: "#060F12",   // 세차 디테일링 아쿠아다크
    borderColor: "#0E2226",
    accentColor: "#3BC9DB", // 아쿠아(비포·디테일·광택 badge)
  },
  maemuldocu: {
    name: "매물투명극장",
    emoji: "🚙",
    barColor: "#071008",   // 투명 매물 공개 그린다크
    borderColor: "#122414",
    accentColor: "#51CF66", // 그린(실물·이력·확인 badge)
  },
  trackdocu: {
    name: "서킷극장",
    emoji: "🏁",
    barColor: "#120606",   // 서킷 주행 레이싱다크
    borderColor: "#241010",
    accentColor: "#FA5252", // 레이싱레드(출발·코너·기록 badge)
  },
  garagedocu: {
    name: "차고지극장",
    emoji: "🛞",
    barColor: "#120E08",   // 차고지 감성 웜다크
    borderColor: "#241C12",
    accentColor: "#E0A458", // 웜앰버(quote-only 인용)
  },
  carlab: {
    name: "차알못연구소",
    emoji: "🧰",
    barColor: "#0A0A14",   // 부품 도해 인디고다크
    borderColor: "#16162A",
    accentColor: "#748FFC", // 인디고(원리·비용·체크 badge)
  },
  bungbungtoon: {
    name: "붕붕툰극장",
    emoji: "🚘",
    barColor: "#16110A",   // 자동차 카툰 시트콤 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFD836", // 옐로(핵심 키워드 mega)
  },
  // 9:16 숏폼 썰 12종 — 골격(SSULCINE 시네마틱·SEOLEMON 훈화/일러·HONEYITEM 다크·SSULLETTER 감성)에 맞춘 채널색.
  siseungssul: {
    name: "체감썰",
    emoji: "🚗",
    barColor: "#0A0B12",   // 시승 시네마틱 건메탈다크(SSULCINE 골격)
    borderColor: "#0A0B12",
    accentColor: "#7C8FF0", // 건메탈블루 채널명
  },
  jeongbissul: {
    name: "정비썰",
    emoji: "🔧",
    barColor: "#100B06",   // 정비 시네마틱 오렌지다크(SSULCINE 골격)
    borderColor: "#100B06",
    accentColor: "#F5A04A", // 오렌지 채널명
  },
  setchassul: {
    name: "질감썰",
    emoji: "✨",
    barColor: "#060F12",   // 세차 시네마틱 아쿠아다크(SSULCINE 골격)
    borderColor: "#060F12",
    accentColor: "#4FD3E3", // 아쿠아 채널명
  },
  maemulssul: {
    name: "흠부터썰",
    emoji: "🔎",
    barColor: "#EAF6EC",   // 연그린 게시판 상단바(SEOLEMON 훈화 골격)
    borderColor: "#C9EFD1", // 소프트 그린 윤곽선
    accentColor: "#2E9E4F", // 매물 그린 딥 강조
  },
  chatipssul: {
    name: "차꿀팁썰",
    emoji: "🧾",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#F5B301", // 체크 골드 강조(meta 줄2)
  },
  chaquizssul: {
    name: "차퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#F5B301", // 정답 골드 강조(퀴즈 정답 — meta 줄2)
  },
  dealertokssul: {
    name: "딜러솔직썰",
    emoji: "🤳",
    barColor: "#ECF1F8",   // 따뜻한 크림블루 상단바(SEOLEMON 훈화 골격)
    borderColor: "#C9D6E8", // 건메탈블루 윤곽선
    accentColor: "#3A5FBF", // 딜러 건메탈블루 딥 강조
  },
  cheotchassul: {
    name: "혼잣말썰",
    emoji: "💭",
    barColor: "#100C10",   // 감성 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C10",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  simyajeongbissul: {
    name: "여백썰",
    emoji: "🌫",
    barColor: "#0A0A10",   // 심야 시네마틱 블루그레이다크(SSULCINE 골격)
    borderColor: "#0A0A10",
    accentColor: "#8FA0C8", // 심야 블루그레이 채널명
  },
  chacutssul: {
    name: "한컷차도해썰",
    emoji: "📇",
    barColor: "#EBEDFB",   // 연인디고 게시판 상단바(SEOLEMON 일러 골격·도해)
    borderColor: "#C9CEF0", // 소프트 인디고 윤곽선
    accentColor: "#4A5AD0", // 도해 인디고 딥 강조
  },
  bungbungtoonssul: {
    name: "붕붕툰썰",
    emoji: "🚘",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격·카툰)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  carwebtoonssul: {
    name: "카웹툰썰",
    emoji: "🖼",
    barColor: "#E9F0FB",   // 연블루 게시판 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#C9DAF0", // 소프트 블루 윤곽선
    accentColor: "#4A78D0", // 웹툰 블루 딥 강조
  },
  // ── ⛪ 종교(교회) 테넌트 특화 20종(회장님 2026-07-21 세계관 확장 Step 14) — 16:9 8종은 종교 스펙 brandedHeader 정본 ──
  malsseumdocu: {
    name: "말씀클립극장",
    emoji: "🎤",
    barColor: "#0C0A12",   // 말씀 클립 딥퍼플다크
    borderColor: "#181226",
    accentColor: "#9775FA", // 딥퍼플(말씀·한 문장·여운 badge)
  },
  gongdongchedocu: {
    name: "공동체극장",
    emoji: "🤝",
    barColor: "#120E08",   // 공동체 온기 웜오렌지다크
    borderColor: "#241C10",
    accentColor: "#FFA94D", // 웜오렌지(준비·함께·온기 badge)
  },
  wirotalk: {
    name: "위로상담소",
    emoji: "💬",
    barColor: "#06110E",   // 위로 상담 틸다크
    borderColor: "#0E241F",
    accentColor: "#38D9A9", // 틸(고민·공감·한 마디 badge)
  },
  naturedocu: {
    name: "자연말씀극장",
    emoji: "🌿",
    barColor: "#071009",   // 자연 서정 세이지다크
    borderColor: "#122414",
    accentColor: "#69DB7C", // 세이지(quote-only 인용)
  },
  mukssangart: {
    name: "새벽묵상극장",
    emoji: "🕯",
    barColor: "#100E08",   // 새벽 묵상 웜다크
    borderColor: "#221E12",
    accentColor: "#E9C46A", // 소프트골드(quote-only 인용)
  },
  callidocu: {
    name: "말씀캘리극장",
    emoji: "✍️",
    barColor: "#0E0C0A",   // 필사 감성 웜다크
    borderColor: "#201C16",
    accentColor: "#8093F1", // 인디고블루(호흡·필사·여백 badge)
  },
  gyohoetoon: {
    name: "교회툰극장",
    emoji: "⛪",
    barColor: "#16110A",   // 교회 카툰 시트콤 웜다크
    borderColor: "#2A2010",
    accentColor: "#FFD65C", // 옐로(핵심 키워드 mega)
  },
  stainedart: {
    name: "빛유리극장",
    emoji: "🪟",
    barColor: "#0A0812",   // 스테인드글라스 바이올렛다크
    borderColor: "#161026",
    accentColor: "#7048E8", // 바이올렛(빛·조각·완성 badge)
  },
  // 9:16 숏폼 썰 12종 — 골격(SEOLEMON 훈화/일러 라이트·SSULCINE 시네마틱·SSULLETTER 감성·HONEYITEM 다크)에 맞춘 채널색.
  malsseumssul: {
    name: "말씀썰",
    emoji: "🎤",
    barColor: "#EDEBFB",   // 연퍼플 게시판 상단바(SEOLEMON 훈화 골격)
    borderColor: "#CBC6F0", // 소프트 퍼플 윤곽선
    accentColor: "#6E56CF", // 말씀 딥퍼플 강조
  },
  gongdongchessul: {
    name: "공동체썰",
    emoji: "🤝",
    barColor: "#100C08",   // 공동체 시네마틱 웜오렌지다크(SSULCINE 골격)
    borderColor: "#100C08",
    accentColor: "#F0A85A", // 웜오렌지 채널명
  },
  wirossul: {
    name: "위로썰",
    emoji: "💬",
    barColor: "#E6F6F1",   // 연틸 게시판 상단바(SEOLEMON 훈화 골격)
    borderColor: "#C6EFE4", // 소프트 틸 윤곽선
    accentColor: "#1C9E82", // 위로 틸 딥 강조
  },
  naturessul: {
    name: "자연말씀썰",
    emoji: "🌿",
    barColor: "#08100C",   // 자연 시네마틱 세이지다크(SSULCINE 골격)
    borderColor: "#08100C",
    accentColor: "#6ECB7E", // 세이지 채널명
  },
  gamsapyeonjissul: {
    name: "손편지썰",
    emoji: "💌",
    barColor: "#100C10",   // 감성 시네마틱 다크 헤더(SSULLETTER 골격)
    borderColor: "#100C10",
    accentColor: "#E5B96A", // 노을 골드 채널명
  },
  mukssangssul: {
    name: "묵상썰",
    emoji: "🕯",
    barColor: "#F3EFDD",   // 연골드 게시판 상단바(SEOLEMON 일러 골격·수채)
    borderColor: "#E6DCBB", // 소프트 골드 윤곽선
    accentColor: "#C79A3A", // 묵상 골드 딥 강조
  },
  callissul: {
    name: "캘리썰",
    emoji: "✍️",
    barColor: "#EBEDFB",   // 연인디고 게시판 상단바(SEOLEMON 일러 골격·캘리)
    borderColor: "#C9CEF0", // 소프트 인디고 윤곽선
    accentColor: "#4A5AD0", // 캘리 인디고 딥 강조
  },
  stainedssul: {
    name: "빛유리썰",
    emoji: "🪟",
    barColor: "#EEEAFB",   // 연바이올렛 게시판 상단바(SEOLEMON 일러 골격·스테인드글라스)
    borderColor: "#D2C6F0", // 소프트 바이올렛 윤곽선
    accentColor: "#5A3FC0", // 빛유리 바이올렛 딥 강조
  },
  gyohoetoonssul: {
    name: "교회툰썰",
    emoji: "⛪",
    barColor: "#FEF3C0",   // 코믹옐로 게시판 상단바(SEOLEMON 일러 골격·카툰)
    borderColor: "#EBD26A", // 옐로 윤곽선
    accentColor: "#F5A623", // 카툰 앰버 강조
  },
  gyohoewebtoonssul: {
    name: "교회웹툰썰",
    emoji: "🖼",
    barColor: "#E9F0FB",   // 연블루 게시판 상단바(SEOLEMON 일러 골격·웹툰)
    borderColor: "#C9DAF0", // 소프트 블루 윤곽선
    accentColor: "#4A78D0", // 웹툰 블루 딥 강조
  },
  malsseumcutssul: {
    name: "한컷말씀썰",
    emoji: "📇",
    barColor: "#E6F2FB",   // 연스카이 게시판 상단바(SEOLEMON 일러 골격·플랫 도해)
    borderColor: "#C9E0F0", // 소프트 스카이 윤곽선
    accentColor: "#2E7FC0", // 도해 스카이 딥 강조
  },
  faithquizssul: {
    name: "신앙퀴즈썰",
    emoji: "❓",
    barColor: "#0E1622",   // 다크 헤더(HONEYITEM 골격)
    borderColor: "#1E2E44",
    accentColor: "#F5B301", // 정답 골드 강조(마음 퀴즈 정답 — meta 줄2)
  },
  ssulfuture: {
    name: "썰미래",
    emoji: "🚀",
    barColor: "#D2ECF8",   // 연시안 상단바
    borderColor: "#5FBFE5", // 시안 윤곽선
    accentColor: "#0A6FA0", // 일렉트릭 블루 강조
  },
  ssuljoseon: {
    name: "썰조선",
    emoji: "🏯",
    barColor: "#E6DCC6",   // 한지 상단바
    borderColor: "#B49B6E", // 황토 윤곽선
    accentColor: "#2C3A4F", // 먹청 강조
  },
  ssulromance: {
    name: "썰순정",
    emoji: "🌸",
    barColor: "#F6E2F1",   // 연라벤더로즈 상단바
    borderColor: "#E6A8D4", // 로즈 윤곽선
    accentColor: "#C0357F", // 진로즈 강조
  },
  ssulmanly: {
    name: "썰사나이",
    emoji: "👊",
    barColor: "#E8D2D0",   // 연적 상단바
    borderColor: "#C07A74", // 적갈 윤곽선
    accentColor: "#7E1C1C", // 다크레드 강조
  },
  ssulgugu: {
    name: "썰구구",
    emoji: "🚂",
    barColor: "#D6DEEE",   // 연네이비 상단바
    borderColor: "#8090C4", // 네이비 윤곽선
    accentColor: "#20306E", // 딥블루 강조
  },
  ssulseo: {
    name: "썰세오",
    emoji: "⭐",
    barColor: "#D6F0F0",   // 연시안 상단바
    borderColor: "#82D2D2", // 시안 윤곽선
    accentColor: "#DB4F95", // 핑크 강조
  },
  empathy: {
    name: "공감썰",
    emoji: "😮‍💨",
    barColor: "#F2E2A2",
    borderColor: "#E2CE84",
    accentColor: "#6B5B2E",
  },
  ssulcine: {
    name: "썰씨네",
    emoji: "🎬",
    barColor: "#0B0B0F",
    borderColor: "#0B0B0F",
    accentColor: "#FFFFFF", // 시네마틱 헤더 채널명 흰색
  },
  // 🕺 모션컨트롤(2026-07-16) — 썰씨네 골격 재사용(클립 쇼츠 — 룩 동일·이름/이모지만 분화)
  motioncontrol: {
    name: "모션컨트롤",
    emoji: "🕺",
    barColor: "#0B0B0F",
    borderColor: "#0B0B0F",
    accentColor: "#FFFFFF",
  },
  // 🌙 1차 배치(2026-07-12) — 썰편지·썰울림·썰심야. barColor=팩토리 헤더 bg 와 일치.
  ssulletter: {
    name: "썰편지",
    emoji: "💌",
    barColor: "#1B0E14",
    borderColor: "#2A1720",
    accentColor: "#F3D9CE", // 시네마틱 헤더 채널명(로즈)
  },
  ssulunse: {
    name: "썰운세",
    emoji: "🔮",
    barColor: "#F7A9B6",
    borderColor: "#F7A9B6",
    accentColor: "#C4451E", // 벤치마크 제목 강조색(실측)
  },
  phonessul: {
    name: "썰통화",
    emoji: "📞",
    barColor: "#000000",
    borderColor: "#1A1A1A",
    accentColor: "#3A3A3A", // 헤더 화살표·장식 — 벤치에 헤더가 없어 배경에 묻는 워터마크 회색(제목 연두 #a0e800 은 제목 색 축이 따로 든다)
  },
  ssulullim: {
    name: "썰울림",
    emoji: "🔥",
    barColor: "#14130E",
    borderColor: "#23231A",
    accentColor: "#F2C14E", // 앰버 강조
  },
  ssulsimya: {
    name: "썰심야",
    emoji: "🕯️",
    barColor: "#241C15",
    borderColor: "#160F0A",
    accentColor: "#E8B968", // 웜앰버
  },
  // 🌐 US 수출 배치(2026-07-12) — 채널명=영어(시장 언어). barColor=팩토리 헤더 bg 와 일치.
  spilltea: {
    name: "SpillTea",
    emoji: "🍵",
    barColor: "#F5EBDD",
    borderColor: "#E8D9C4",
    accentColor: "#B9A88F",
  },
  nightfile: {
    name: "Nightfile",
    emoji: "🕵️",
    barColor: "#0A0E12",
    borderColor: "#141C24",
    accentColor: "#7FB4D9", // 콜드 스틸블루
  },
  threadroom: {
    name: "ThreadRoom",
    emoji: "🧵",
    barColor: "#1A1B1B",
    borderColor: "#2E2F2F",
    accentColor: "#FF4500", // 포럼 오렌지
  },
  factpop: {
    name: "FactPop",
    emoji: "💡",
    barColor: "#0F52BA",
    borderColor: "#0B3E8D",
    accentColor: "#FFD54A", // 코발트+옐로 팝
  },
  // 🌐 JP 수출 배치(2026-07-12) — 채널명=일본어(시장 언어).
  kakiko: {
    name: "カキコ",
    emoji: "📝",
    barColor: "#EAE5C9",
    borderColor: "#D8D2B0",
    accentColor: "#800000", // 2ch 스레 제목 적갈
  },
  sukatto: {
    name: "スカッと逆転劇",
    emoji: "⚡",
    barColor: "#14100C",
    borderColor: "#241C14",
    accentColor: "#FF3B30", // 드라마 레드
  },
  nakerucine: {
    name: "泣けるシネマ",
    emoji: "🎗",
    barColor: "#12100E",
    borderColor: "#221E1A",
    accentColor: "#E8C9A8", // 세피아 웜
  },
  naruhodo: {
    name: "なるほど雑学",
    emoji: "💡",
    barColor: "#CFE6F5",
    borderColor: "#B4D4EA",
    accentColor: "#14507E", // 교육 블루
  },
  // 🌐 US 수출 2차(2026-07-13)
  goldmind: {
    name: "GoldMind",
    emoji: "🏛",
    barColor: "#0A0A0A",
    borderColor: "#1E1B14",
    accentColor: "#F5C518", // 스토익 골드
  },
  desknotes: {
    name: "DeskNotes",
    emoji: "📓",
    barColor: "#FAFAF7",
    borderColor: "#E4E4DC",
    accentColor: "#5B7A94", // 스틸블루 포인트
  },
  valuedrop: {
    name: "ValueDrop",
    emoji: "💰",
    barColor: "#101010",
    borderColor: "#242424",
    accentColor: "#FFD400", // 밸류밤 옐로
  },
  marginnote: {
    name: "MarginNote",
    emoji: "📓",
    barColor: "#EFEAE1",   // 웜 리넨(라이트)
    borderColor: "#E2DBCE",
    accentColor: "#9C6B72", // 더스티 모브
  },
  // ── spec 계열 ──
  listicle: {
    name: "재다",
    emoji: "📋",
    barColor: "#82B7F6",   // 파란 헤더
    borderColor: "#DBDBDB", // 회색 제목바
    accentColor: "#2878F4", // 로고 색
  },
  ranking: {
    name: "썰탑",
    emoji: "🏆",
    barColor: "#0D0D0D",   // 다크 배경(랭킹 카운트다운)
    borderColor: "#161616", // 헤더 바
    accentColor: "#FFC83D", // 금색(1위·로고 강조)
  },
  versus: {
    name: "썰대결",
    emoji: "⚔️",
    barColor: "#101018",   // 다크 배경(2분할 대치)
    borderColor: "#1A1A24", // 경계
    accentColor: "#FFD93D", // VS 배지 노랑
  },
  kchat: {
    name: "톡썰",
    emoji: "💬",
    barColor: "#B2C7D9",   // 카톡 연블루 채팅 배경(벤치 417만 실측)
    borderColor: "#A4BACD", // 상단 채팅방 바
    accentColor: "#FEE500", // 노랑 버블(나)
  },
  eokssul: {
    name: "억썰",
    emoji: "💰",
    barColor: "#000000",   // 순검정(SSULNIGHT 계열)
    borderColor: "#0A0A0A",
    accentColor: "#FFD24A", // 골드(숫자 강조 — 썰한밤 형광노랑과 구별)
  },
  sangdamssul: {
    name: "상담썰",
    emoji: "💌",
    barColor: "#FBF6EF",   // 따뜻한 크림(EMPATHY 밝은 배경 계열)
    borderColor: "#E8D9C8",
    accentColor: "#C97B63", // 테라코타(위로 무드)
  },
  ssulmisik: {
    name: "썰미식",
    emoji: "🍜",
    barColor: "#2B1F1A",   // 딥브라운(감성 식당 조명)
    borderColor: "#3A2A22",
    accentColor: "#FF9E3D", // 따뜻한 주황(식욕)
  },
  issuepick: {
    name: "이슈픽",
    emoji: "📌",
    barColor: "#F4F6F8",   // 밝은 그레이(게시판 룩)
    borderColor: "#D7DDE3",
    accentColor: "#E4573D", // 핀 레드(이슈 강조)
  },
  jangbissul: {
    name: "장비썰",
    emoji: "🖥",
    barColor: "#DFF2EA",   // 연민트 헤더(장비빨 실측)
    borderColor: "#C9E6DA",
    accentColor: "#00A79B", // 시안(모델명)
  },
  teukgassul: {
    name: "특가썰",
    emoji: "🏷",
    barColor: "#000000",   // 순검정(SSULNIGHT 계열)
    borderColor: "#0A0A0A",
    accentColor: "#FFD400", // 특가 노랑
  },
  // 🧭 #169 산업별 원리 설명형 — 템플릿 골격/오버레이 스킨은 하나, 헤더 워터마크만 산업별로 분화.
  hyeonjangwonri: { name: "현장의 원리", emoji: "🏗", barColor: "#09131A", borderColor: "#17313D", accentColor: "#F59E0B" },
  bojangwonri: { name: "보장의 원리", emoji: "🛡", barColor: "#0A1420", borderColor: "#18324B", accentColor: "#60A5FA" },
  sijangwonri: { name: "시장의 원리", emoji: "📈", barColor: "#0B1220", borderColor: "#1E334D", accentColor: "#38BDF8" },
  dosiwonri: { name: "도시의 원리", emoji: "🏛", barColor: "#101820", borderColor: "#26394A", accentColor: "#2DD4BF" },
  baeumwonri: { name: "배움의 원리", emoji: "🧒", barColor: "#141326", borderColor: "#302C58", accentColor: "#A78BFA" },
  jepumwonri: { name: "제품의 원리", emoji: "🛍", barColor: "#1B1310", borderColor: "#3A271E", accentColor: "#FB923C" },
  jangsawonri: { name: "장사의 원리", emoji: "🏪", barColor: "#13180E", borderColor: "#2C3A1D", accentColor: "#A3E635" },
  sageonwonri: { name: "사건의 원리", emoji: "⚖️", barColor: "#141016", borderColor: "#312438", accentColor: "#C084FC" },
  jipwonri: { name: "집의 원리", emoji: "🏠", barColor: "#15120E", borderColor: "#34291F", accentColor: "#FBBF24" },
  hapgyeokwonri: { name: "합격의 원리", emoji: "🎓", barColor: "#101827", borderColor: "#24334B", accentColor: "#60A5FA" },
  hanguksaenghwalwonri: { name: "한국생활의 원리", emoji: "🌏", barColor: "#0D1A18", borderColor: "#1F3934", accentColor: "#5EEAD4" },
  momwonri: { name: "몸의 원리", emoji: "⚕️", barColor: "#0D1920", borderColor: "#203946", accentColor: "#67E8F9" },
  jadongchawonri: { name: "자동차의 원리", emoji: "🚗", barColor: "#10131A", borderColor: "#252D3C", accentColor: "#94A3B8" },
  malsseummacrak: { name: "말씀의 맥락", emoji: "🕊", barColor: "#14120D", borderColor: "#332B1A", accentColor: "#E9C46A" },
  ranklist: {
    name: "썰순위",
    emoji: "🥇",
    barColor: "#000000",   // 순검정(풀블리드 레터박스·띵고류 실측)
    borderColor: "#101010", // 경계
    accentColor: "#FFD60A", // 타이틀 줄2 노랑(실측)
  },
  gunrimbo: {
    name: "군림보",
    emoji: "⚡",
    barColor: "#000000",   // 검정 배경
    borderColor: "#000000", // 자막 외곽선
    accentColor: "#F0F800", // 노랑 자막/제목 2줄
  },
  gwajang: {
    name: "과장형",
    emoji: "🤯",
    barColor: "#000000",   // 검정 배경(군림보 골격 동일)
    borderColor: "#000000", // 자막 외곽선
    accentColor: "#FFE83D", // 노랑 자막/제목 2줄(코믹 톤 살짝 밝은 노랑)
  },
  shonen: {
    name: "소년만화",
    emoji: "🔥",
    barColor: "#000000",   // 검정 배경(군림보 골격 동일)
    borderColor: "#000000", // 자막 외곽선
    accentColor: "#FFE83D", // 노랑 자막/제목 2줄
  },
  shojo: {
    name: "순정만화",
    emoji: "🌸",
    barColor: "#000000",   // 검정 배경(군림보 골격 동일)
    borderColor: "#000000", // 자막 외곽선
    accentColor: "#FFE83D", // 노랑 자막/제목 2줄
  },
  shomaru: {
    name: "숏마루",
    emoji: "📺",
    barColor: "#000000",   // 검정 배경
    borderColor: "#FF8A00", // 제목 밑줄 바(주황)
    accentColor: "#FFD400", // 노랑 키워드 강조
  },
  community: {
    name: "썰톡",
    emoji: "💬",
    barColor: "#D7263D",   // 빨강 헤더
    borderColor: "#E8505B", // 태그 배경(연빨강)
    accentColor: "#D7263D", // 댓글 작성자명(빨강)
  },
  health: {
    name: "건강썰",
    emoji: "🥛",
    barColor: "#000000",   // 풀스크린 사진(헤더 없음 — 대표색 미사용)
    borderColor: "#FF2B2B", // 빨강 CTA
    accentColor: "#FFE236", // 노랑 키워드
  },
  board: {
    name: "부추전",
    emoji: "🧅",
    barColor: "#C9E7F5",   // 파스텔 블루 헤더
    borderColor: "#EFEFEF", // 제목바 배경
    accentColor: "#1A2B33", // 헤더 글자색
  },
};

/** 스타일 id 의 채널 테마. 미등록이면 undefined(템플릿 내장 기본값으로 폴백). */
export function getChannelTheme(id: string): ChannelTheme | undefined {
  return CHANNEL_THEMES[id];
}

// ─────────────────────────────────────────────────────────────
// 테넌트 런타임 오버라이드 — 코드 기본값(CHANNEL_THEMES) 위에 테넌트가 화면에서 바꾼 값을 얹는다.
// DB(channel_theme_overrides) → channel-theme-store.ts 로 로드/저장. 여기선 "병합·적용"만(순수).
// ★템플릿 9종은 안 건드린다★: 렌더 data 의 색/이름 필드를 applyChannelThemeToData 로 덮어쓴다.
// ─────────────────────────────────────────────────────────────

/** 테넌트가 화면에서 바꾼 부분 오버라이드. 지정된 필드만 기본값을 덮어쓴다(빈 값은 무시). */
export type ChannelThemeOverride = Partial<ChannelTheme>;

/** 기본 테마 + 테넌트 오버라이드 병합. 오버라이드의 빈/누락 필드는 기본값 유지. */
export function resolveChannelTheme(id: string, override?: ChannelThemeOverride | null): ChannelTheme | undefined {
  const base = CHANNEL_THEMES[id];
  if (!base) return base;
  if (!override) return base;
  const pick = <K extends keyof ChannelTheme>(k: K): ChannelTheme[K] => {
    const v = override[k];
    return v == null || v === "" ? base[k] : (v as ChannelTheme[K]);
  };
  return {
    name: pick("name"),
    emoji: pick("emoji"),
    barColor: pick("barColor"),
    borderColor: pick("borderColor"),
    accentColor: pick("accentColor"),
    channelArt: override.channelArt ?? base.channelArt,
  };
}

/**
 * 렌더 data 에 채널 테마(이름·색)를 입힌다 — 스타일 계열별로 해당 필드를 덮어쓴 새 data 반환(불변).
 * 템플릿/SAMPLE 코드는 그대로 두고, 썸네일·제작 시 이 함수로 테넌트 브랜딩을 주입한다.
 * (각 계열 매핑은 channel-theme.ts ↔ 각 템플릿 배선과 동일: bar=대표, border=보조, accent=강조)
 */
export function applyChannelThemeToData<T>(styleId: string, data: T, theme?: ChannelTheme | null): T {
  if (!theme || !data || typeof data !== "object") return data;
  const d = data as Record<string, unknown>;
  // 직접수정으로 디자인을 잠근 spec 코드(top-level themeLocked)는 재주입을 건너뛴다.
  //   story 계열은 아래 case 의 header.themeLocked 로 자체 처리(이 top-level 플래그는 spec 직접수정 전용).
  if (d.themeLocked === true) return data;
  const clone: Record<string, unknown> = { ...d };
  const obj = (v: unknown) => (v && typeof v === "object" ? { ...(v as Record<string, unknown>) } : {});
  if (isRegionalStyleId(styleId)) {
    const header = obj(d.header);
    if (header.themeLocked === true) {
      clone.header = header;
      return clone as T;
    }
    header.title = theme.name;
    header.bg = theme.barColor;
    header.line = theme.borderColor;
    header.emoji = theme.emoji;
    header.accent = theme.accentColor;
    clone.header = header;
    if (d.meta) {
      const meta = obj(d.meta);
      meta.color = theme.accentColor;
      clone.meta = meta;
    }
    return clone as T;
  }
  switch (styleId) {
    case "seolemon":
    case "ssulmidlife":
    case "ssulghost":
    case "ssulfuture":
    case "ssuljoseon":
    case "ssulromance":
    case "ssulmanly":
    case "ssulgugu":
    case "ssulseo":
    case "ssuljunji":
    case "ssulseuseu":
    case "ssulgothic":
    case "seolpogun":
    case "seoltteurak":
    case "seoldanjjak":
    case "seolnoeul":
    case "seolbomnal":
    case "seolchueok":
    case "seoldambaek":
    case "seolvintage":
    case "ssulmovie":
    case "ssulsuchae":
    case "ssulflat":
    case "ssulcomic":
    case "ssulcrepas":
    case "ssulneon":
    case "ssulmuk":
    case "ssulclay":
    case "ssulanime":
    case "ssulfilm":
    case "ssulpixel":
    case "ssulpocalypse":
    case "ssulkaiju":
    // 🎀 썰핑크류 세계관 18종 Wave 1(2026-08-20) — 썰핑크 골격 공유라 헤더 테마 주입 경로도 동일.
    case "ssulregress":
    case "ssulcider":
    case "ssulzombie":
    case "ssulacademy":
    case "ssulolympus":
    case "ssulhunter":
    case "ssuloffice":
    case "ssuldetect":
    case "ssuldokkaebi":
    case "ssulseonhyeop":
    case "ssulmurim":
    case "ssulmedieval":
    case "ssulsaga":
    case "ssulpharaoh":
    case "ssulvoyage":
    case "ssulmecha":
    case "ssulmaou":
    case "ssuldisaster":
    // 🎨 하위 화풍 승격 배치 A 29종(2026-08-21) — 썰핑크 골격 공유라 헤더 테마 주입 경로도 동일.
    case "ssulmongle":
    case "ssulchorok":
    case "ssulnemo":
    case "ssulorigi":
    case "ssulpopcorn":
    case "ssulkkoma":
    case "ssulyagwang":
    case "ssulminhwa":
    case "ssulseonbi":
    case "ssuldaenggi":
    case "ssulhanyang":
    case "ssulpungsok":
    case "ssulgunggwol":
    case "ssulhyeopgaek":
    case "ssulyeonbun":
    case "ssulseollem":
    case "ssulmulbit":
    case "ssuldugeun":
    case "ssuljangmi":
    case "ssulkongdak":
    case "ssulbaram":
    case "ssulinhwa":
    case "ssulhero":
    case "ssulnoir":
    case "ssulbulkkot":
    case "ssulgeojang":
    case "ssulmukgaek":
    case "ssulgangcheol":
    case "ssuldesaeng":
    // 🎨 하위 화풍 승격 배치 B 18종(2026-08-21) — 썰핑크 골격 공유라 헤더 테마 주입 경로도 동일.
    case "ssulyogoe":
    case "ssulbeonjim":
    case "ssulmoktan":
    case "ssulsindang":
    case "ssulppetit":
    case "ssulmanga":
    case "ssulgongryak":
    case "ssulsegimal":
    case "ssulfigure":
    case "ssultumyeong":
    case "ssulsaegyeonpil":
    case "ssulcheongryang":
    case "ssultoy":
    case "ssulleijeo":
    case "ssuldwitgolmok":
    case "ssulposter":
    case "ssulmalgeum":
    // 📚 범용 썰 장르 스타일 23종(2026-08-21) — 썰핑크 골격 공유라 헤더 테마 주입 경로도 동일.
    case "ssuluimun":
    case "ssulmanyak":
    case "ssulseoljeong":
    case "ssulsinhwa":
    case "ssulsaengjon":
    case "ssulscam":
    case "ssulsoreum":
    case "ssulsimri":
    case "ssulsangsik":
    case "ssulgwanchal":
    case "ssulbeonyeok":
    case "ssulsiseon":
    case "ssulgeukhan":
    case "ssulheungmang":
    case "ssulgyeongmae":
    case "ssulguchul":
    case "ssulretro":
    case "ssulbamsayeon":
    case "ssulfact":
    case "ssulgeunal":
    case "ssuljiri":
    case "ssullocal": // 📍 지역썰(SEOLEMON 게시판 골격) — 헤더 + meta(작성자·조회수 줄) 강조색 테마 주입
    case "ssulsingirok":
    case "ssulbihind":
    case "ssulpopup":
    case "ssuljasu":
    case "ssulgeurimja":
    case "ssulbeullok":
    case "ssulvoxel":
    case "ssullowpoly":
    case "ssulscratch":
    case "ssulbyeokhwa":
    case "ssulmosaic":
    case "ssuldongpan":
    case "ssulukiyo":
    case "ssulcheongsajin":
    case "ssulmannyeonpil":
    case "ssulartdeco":
    case "ssuloetgwanggo":
    case "ssulrubber":
    case "ssulcollage":
    case "ssuly2k":
    case "ssulcamcorder":
    case "ssulpolla":
    case "ssulbot":
    case "ssuldocu":
    case "ssulcinema":
    case "ssuldaily":
    case "ssulmono":
    case "ssulleim":
    case "ssulgrium":
    case "ssulsaebyeok":
    case "ssulhaesal":
    case "ssultem":
    case "ssulshop":
    // 🍯 꿀템썰 패밀리(쇼핑 쇼츠 변주 11종 — 회장님 2026-07-12): 헤더 + ★타이틀 줄2(meta) 강조색★까지
    //   테마가 정본 — 변주가 makeHoneyItemTemplate 하나를 공유하므로 색은 여기서만 갈아입는다.
    case "honeyitem":
    case "godtem":
    case "gatseongbi":
    case "pickshot":
    case "matfriends":
    case "hamshort":
    case "jaemishow":
    case "snackpocket":
    case "jiwoomung":
    case "dalleshop":
    case "hyungnim":
    case "igeone":
    case "jachissul": // 자취요리(SSULSHOP 골격) — 헤더 + meta(줄2) 강조색 테마 주입
    case "ssultech":  // 테크 가젯(HONEYITEM 골격) — 헤더 + meta(줄2) 강조색 테마 주입
    case "ssulmisik": // 썰미식(SSULSHOP 골격) — 헤더 + meta 강조색
    case "issuepick": // 이슈픽(SEOLEMON 골격) — 헤더 + meta 강조색
    case "safequiz":  // 안전퀴즈썰(HONEYITEM 골격) — 헤더 + meta(정답 줄2) 강조색
    case "jeomgumssul": // 점검썰(HONEYITEM 골격) — 헤더 + meta(줄2) 강조색
    // 🛡 보험 9:16 HONEYITEM 골격 2종 — 헤더 + meta(정답·핵심 줄2) 강조색
    case "bojangquiz":  // 보장퀴즈썰
    case "cheonggussul": // 꿀팁썰
    // 📈 증권 9:16 HONEYITEM 골격 2종 — 헤더 + meta(정답·핵심 줄2) 강조색
    case "tojaquiz":    // 투자퀴즈썰
    case "checkssul":   // 체크썰
    // 🏛 공공 9:16 HONEYITEM 골격 2종 — 헤더 + meta(정답·핵심 줄2) 강조색
    case "townquizssul": // 동네퀴즈썰
    case "annaessul":    // 안내썰
    // 🧒 교육 9:16 HONEYITEM 골격 2종 — 헤더 + meta(정답·핵심 줄2) 강조색
    case "eduquizssul":  // 교실퀴즈썰
    case "studytipssul": // 학습꿀팁썰
    // 🛍 커머스 9:16 HONEYITEM 골격 3종 — 헤더 + meta(정답·핵심·after 줄2) 강조색
    case "pickquizssul": // 골라보기썰
    case "sotipssul":    // 쇼핑꿀팁썰
    case "beforessul":   // 비포애프터썰(전후 비교)
    // 🏪 소상공인 9:16 HONEYITEM 골격 2종 — 헤더 + meta(정답·핵심 줄2) 강조색
    case "changupssul":     // 체크썰
    case "jangsaquizssul":  // 장사퀴즈썰
    // ⚖️ 전문직 9:16 HONEYITEM 골격 2종 — 헤더 + meta(정답·핵심 줄2) 강조색
    case "silmussul":       // 실무꿀팁썰
    case "proquizssul":     // 전문퀴즈썰
    // 🎓 입시교육 9:16 HONEYITEM 골격 2종 — 헤더 + meta(체크·정답 줄2) 강조색
    case "jeonlyakssul":    // 전략썰
    case "ipsiquizssul":    // 입시퀴즈썰
    // ⛪ 종교 9:16 HONEYITEM 골격 1종 — 헤더 + meta(마음 퀴즈 정답 줄2) 강조색
    case "faithquizssul":   // 신앙퀴즈썰
    {
      const header = obj(d.header);
      if (header.themeLocked === true) {
        clone.header = header;
        return clone as T;
      }
      header.title = theme.name;
      header.bg = theme.barColor;
      header.line = theme.borderColor;
      header.emoji = theme.emoji;
      header.accent = theme.accentColor;
      clone.header = header;
      if (d.meta) {
        const meta = obj(d.meta);
        meta.color = theme.accentColor;
        clone.meta = meta;
      }
      return clone as T;
    }
    // 🏦 KB 다이렉트형(크랩스 전용) — ★헤더만 재색 그룹★. meta 강조 그룹(seolemon 계열)에 넣으면
    //   theme.accentColor 가 meta.color 를 덮어 원본의 **검정 메타**가 회갈색으로 바뀐다.
    case "kbdirect":
    case "ssulnight":
    case "knowdoc":
    case "seniorlife": // 지식다큐(16:9 오버레이) — 워터마크 헤더만 재색(렌더러는 header 바 미렌더)
    case "econstory": // 머니툰(16:9 오버레이) — 워터마크 헤더만 재색(렌더러는 header 바 미렌더)
    case "infoknow": // 똑똑백과(16:9 오버레이) — 워터마크 헤더만 재색(렌더러는 header 바 미렌더)
    case "cosmos":     // 🎞 16:9 2차 배치 6종(2026-07-17) — 전부 워터마크 헤더만 재색
    case "nightbook":
    case "darkcase":
    case "famefall":
    case "sleepdocu":
    case "horrorrank":
    case "stickfall":  // 🎞 3차 배치 2종(2026-07-18) — 워터마크 헤더만 재색
    case "jinsei":
    case "gyakuten":   // 🎞 4차 배치 11종(2026-07-18 "풀완비") — 전부 워터마크 헤더만 재색
    case "sengoku":
    case "kaidan":
    case "teikoku":
    case "nemureru":
    case "yadam":
    case "verdict":
    case "geomap":
    case "wisdom":
    case "gunggeum":
    case "trivia":
    case "blueprint": // 📐 설계도 가족(기관/현장/기업/강의) — 워터마크 헤더만 재색
    case "blueprintSite":
    case "blueprintCorp":
    case "blueprintLecture":
    case "blueprintMotion":
    case "blueprintShowreel":
    case "blueprintColorCut":
    case "briefing": // 📊 브리핑극장 — 워터마크 헤더만 재색
    // 🧭 #169 원리 설명형 16:9 — 공유 오버레이 위 워터마크 헤더만 산업별로 재색.
    case "hyeonjangwonri":
    case "bojangwonri":
    case "sijangwonri":
    case "dosiwonri":
    case "baeumwonri":
    case "jepumwonri":
    case "jangsawonri":
    case "sageonwonri":
    case "jipwonri":
    case "hapgyeokwonri":
    case "hanguksaenghwalwonri":
    case "momwonri":
    case "jadongchawonri":
    case "malsseummacrak":
    case "teukgassul": // 특가썰(SSULNIGHT 골격) — 헤더만 재색
    case "jangbissul": // 장비썰 — 헤더만 재색(★meta=스펙 요약줄 검정 유지 — accent 시안이 노랑 밴드 위 대비 2.4:1 로 깨짐·Codex MAJOR)
    case "eokssul":     // 억썰(SSULNIGHT 골격) — 헤더만 재색
    case "sangdamssul": // 상담썰(EMPATHY 골격) — 헤더만 재색
    case "empathy":
    case "ssulcine":
    case "motioncontrol": // 🕺 썰씨네 골격 재사용
    case "ssulletter":
    case "ssulunse":
    case "phonessul":
    case "ssulullim":
    case "ssulsimya":
    case "spilltea":
    case "nightfile":
    case "threadroom":
    case "factpop":
    case "kakiko":
    case "sukatto":
    case "nakerucine":
    case "naruhodo":
    case "goldmind":
    case "desknotes":
    case "valuedrop":
    case "marginnote":
    // 🏗 배치5 건설·산업안전 — 16:9 3종(오버레이 워터마크 헤더)+9:16 5종(SSULCINE·SEOLEMON 골격) 헤더만 재색.
    case "gongdocu":
    case "safetoon":
    case "zoosite":
    case "achassul":     // 아차사고썰(SSULCINE 골격)
    case "banjangssul":  // 김반장썰(SEOLEMON 게시판 골격)
    case "sitetoon":     // 현장툰썰(SEOLEMON 일러 골격)
    case "zoocrew":      // 동물반장썰(SEOLEMON 일러 골격)
    // 🏗 배치5b 건설·산업안전 확장 — 16:9 5종(오버레이 워터마크 헤더)+9:16 5종(SEOLEMON·SSULCINE·SSULLETTER 골격) 헤더만 재색.
    case "courtcase":    // 판례극장(16:9 오버레이)
    case "structlab":    // 구조연구소(16:9 오버레이)
    case "myungjang":    // 명장록(16:9 오버레이)
    case "sagorecord":   // 현장연대기(16:9 오버레이)
    case "safenomics":   // 안전경제(16:9 오버레이)
    case "sagobrief":    // 사고 사례 브리핑(16:9 오버레이)
    case "sojangMorning": // 아침 안전 조회(16:9 설계도 틀 · 헤더 숨김)
    case "sinipssul":    // 신입일기썰(SEOLEMON 게시판 골격)
    case "sangsikssul":  // 안전상식썰(SEOLEMON 트리비아 골격)
    case "gujossul":     // 한컷구조썰(SEOLEMON 트리비아 골격·블루프린트 도해)
    case "yaganssul":    // 야간현장썰(SSULCINE 시네마틱 골격)
    case "pyeonjissul":  // 가족편지썰(SSULLETTER 감성 편지 골격)
    // 🛡 보험 — 16:9 8종(오버레이 워터마크 헤더)+9:16 10종(SSULCINE·SEOLEMON·SSULLETTER 골격) 헤더만 재색(퀴즈·꿀팁 2종=meta 강조 그룹).
    case "insucase":     // 보상극장(16:9 오버레이)
    case "yakgwanlab":   // 약관연구소(16:9 오버레이)
    case "lifeplan":     // 인생보장극장(16:9 오버레이)
    case "fraudfile":    // 추적파일(16:9 오버레이)
    case "insushow":     // 보험툰극장(safetoon 16:9 오버레이·카툰)
    case "fpchronicle":  // 인연록(16:9 오버레이)
    case "moneyclass":   // 칠판클래스(16:9 오버레이·화이트보드)
    case "insumate":     // 보험메이트(16:9 오버레이)
    case "bosangssul":   // 실화썰(SSULCINE 골격)
    case "fpssul":       // 김설계사썰(SEOLEMON 게시판 골격)
    case "fraudssul":    // 기록썰(SEOLEMON 흑백 다큐 골격)
    case "yakgwanssul":  // 한줄썰(SEOLEMON 트리비아 골격·플랫 일러)
    case "gajokssul":    // 가족일러썰(SEOLEMON 수채 일러 골격)
    case "pixelssul":    // 픽셀썰(SEOLEMON 게시판 골격·seolemon 변주·픽셀)
    case "clayssul":     // 클레이썰(SEOLEMON 일러 골격·seolemon 변주·클레이)
    case "promisessul":  // 남긴약속썰(SSULLETTER 감성 골격)
    case "webtoonssul":  // 웹툰썰(SEOLEMON 일러 골격·seolemon 변주·웹툰)
    case "insutoon":     // 보험툰썰(SEOLEMON 웹툰풍 코믹 카툰 골격)
    // 📈 증권 — 16:9 8종(오버레이 워터마크 헤더)+9:16 10종(SSULCINE·SEOLEMON·SSULLETTER 골격) 헤더만 재색(퀴즈·체크 2종=meta 강조 그룹).
    case "marketbrief":  // 마켓브리핑(16:9 오버레이)
    case "analystnote":  // 전문가노트(16:9 오버레이)
    case "mindtheater":  // 투자심리극장(16:9 오버레이)
    case "finhistory":   // 시장의역사(16:9 오버레이)
    case "chartlab":     // 차트연구소(16:9 오버레이)
    case "virtualdesk":  // 버추얼데스크(16:9 오버레이)
    case "antshow":      // 개미극장(safetoon 16:9 오버레이·카툰)
    case "moneypress":   // 머니신문(16:9 오버레이)
    case "tojassul":     // 투자실화썰(SSULCINE 골격)
    case "pbssul":       // 김PB썰(SEOLEMON 게시판 골격)
    case "docussul":     // 일지썰(SEOLEMON 흑백 다큐 골격)
    case "miraessul":    // 미래편지썰(SSULLETTER 감성 골격)
    case "anttoon":      // 개미툰썰(SEOLEMON 웹툰풍 코믹 카툰 골격)
    case "moneypixel":   // 머니픽셀썰(SEOLEMON 게시판 골격·픽셀)
    case "origamissul":  // 종이접기썰(SEOLEMON 일러 골격·종이)
    case "antwebtoon":   // 개미웹툰썰(SEOLEMON 일러 골격·웹툰)
    case "chartssul":    // 한컷차트썰(SEOLEMON 트리비아 골격·플랫 도해)
    case "minicityssul": // 미니시티썰(SEOLEMON 일러 골격·미니어처)
    // 🏛 공공 — 16:9 8종(오버레이 워터마크 헤더)+9:16 10종(SEOLEMON·SSULCINE·SSULLETTER 골격) 헤더만 재색(퀴즈·안내 2종=meta 강조 그룹).
    case "policybrief":  // 혜택브리핑(16:9 오버레이)
    case "towntalk":     // 타운톡(16:9 오버레이)
    case "localdocu":    // 우리고장극장(16:9 오버레이·quote-only)
    case "psatheater":   // 공익극장(16:9 오버레이)
    case "signageloop":  // 전광판극장(16:9 오버레이·무음 송출면)
    case "townmate":     // 동네지킴이(16:9 오버레이·마스코트)
    case "steplab":      // 절차연구소(16:9 오버레이·도해)
    case "towncartoon":  // 동네만평(16:9 오버레이·만평 카툰)
    case "simunssul":    // 시민문답썰(SEOLEMON 게시판 골격)
    case "jumugwanssul": // 주무관썰(SEOLEMON 게시판 골격)
    case "psassul":      // 공익재연썰(SSULCINE 시네마틱 골격)
    case "gohyangssul":  // 고향편지썰(SSULLETTER 감성 골격)
    case "dongnematssul": // 동네발견썰(SSULCINE 시네마틱 골격)
    case "gayossul":     // 동네가요썰(SEOLEMON 골격)
    case "townwebtoonssul": // 동네웹툰썰(SEOLEMON 일러 골격·웹툰)
    case "dohaessul":    // 한컷도해썰(SEOLEMON 트리비아 골격·플랫 도해)
    case "feltssul":     // 인형극썰(SEOLEMON 일러 골격·펠트 인형)
    case "seasonssul":   // 사계절썰(SEOLEMON 수채 일러 골격)
    // 🧒 교육 — 16:9 8종(오버레이 워터마크 헤더)+9:16 10종(SEOLEMON·SSULCINE·SSULLETTER 골격) 헤더만 재색(퀴즈·꿀팁 2종=meta 강조 그룹).
    case "parenttalk":   // 학부모상담소(16:9 오버레이)
    case "classlive":    // 맛보기교실(16:9 오버레이)
    case "growthdocu":   // 성장극장(16:9 오버레이·quote-only)
    case "fiveguide":    // 오분가이드(16:9 오버레이)
    case "edumate":      // 공부친구(16:9 오버레이·마스코트)
    case "conceptlab":   // 개념연구소(16:9 오버레이·도해)
    case "studytoon":    // 학습만화극장(16:9 오버레이·학습만화)
    case "storyart":     // 동화극장(16:9 오버레이·그림책 낭독)
    case "gominssul":    // 고민썰(SEOLEMON 게시판 골격)
    case "teacherssul":  // 선생님썰(SEOLEMON 게시판 골격)
    case "eungwonssul":  // 응원편지썰(SSULLETTER 감성 골격)
    case "schooldayssul": // 아이시선썰(SSULCINE 시네마틱 골격)
    case "matessul":     // 공부친구썰(SEOLEMON 일러 골격·캐릭터)
    case "edutoonssul":  // 학습툰썰(SEOLEMON 일러 골격·학습만화)
    case "edupixelssul": // 픽셀교실썰(SEOLEMON 게시판 골격·픽셀)
    case "gaenyeomssul": // 한컷개념썰(SEOLEMON 트리비아 골격·플랫 도해)
    case "dollssul":     // 인형놀이썰(SEOLEMON 일러 골격·인형·클레이)
    case "donghwassul":  // 동화썰(SEOLEMON 수채 일러 골격)
    // 🛍 커머스 — 16:9 8종(오버레이 워터마크 헤더)+9:16 10종(SSULCINE·SEOLEMON·SSULLETTER 골격) 헤더만 재색(골라보기·쇼핑꿀팁·비포애프터 3종=meta 강조 그룹).
    case "luxereview":   // 럭셔리쇼룸(16:9 오버레이·리뷰)
    case "studioreview": // 스튜디오리뷰(16:9 오버레이·리뷰)
    case "homereview":   // 홈리뷰(16:9 오버레이·리뷰)
    case "duotalk":      // 듀오리뷰(16:9 오버레이·토크)
    case "brandstory":   // 브랜드연대기(16:9 오버레이·quote-only)
    case "productlab":   // 상품연구소(16:9 오버레이·도해)
    case "shoptoon":     // 마트툰극장(16:9 오버레이·카툰)
    case "minimart":     // 미니어처마켓(16:9 오버레이·디오라마)
    case "jangbossul":   // 장바구니썰(SSULCINE 시네마틱 골격)
    case "mdssul":       // 김MD썰(SEOLEMON 훈화 골격)
    case "unboxssul":    // 언박싱썰(SSULCINE 시네마틱 골격)
    case "soljikssul":   // 솔직썰(SEOLEMON 훈화 골격)
    case "dangolssul":   // 단골썰(SSULLETTER 감성 골격)
    case "shoptoonssul": // 마트툰썰(SEOLEMON 일러 골격·카툰)
    case "sangpumssul":  // 한컷상품썰(SEOLEMON 트리비아 골격·플랫 도해)
    case "minimartssul": // 미니마켓썰(SEOLEMON 일러 골격·미니어처)
    case "martwebtoonssul": // 마트웹툰썰(SEOLEMON 일러 골격·웹툰)
    // 🏪 소상공인 — 16:9 8종(오버레이 워터마크 헤더)+9:16 10종(SSULCINE·SEOLEMON·SSULLETTER 골격) 헤더만 재색(창업·퀴즈 2종=meta 강조 그룹).
    case "dalindocu":    // 달인극장(16:9 오버레이)
    case "sajangdocu":   // 사장의하루(16:9 오버레이)
    case "golmokdocu":   // 골목연대기(16:9 오버레이·quote-only)
    case "jangsaclass":  // 장사교실(16:9 오버레이)
    case "munjeondocu":  // 문전성시극장(16:9 오버레이)
    case "sigdocu":      // 시그니처극장(16:9 오버레이·B롤)
    case "jangsalab":    // 장사연구소(16:9 오버레이·도해)
    case "golmoktoon":   // 골목툰극장(16:9 오버레이·카툰)
    case "dalinssul":    // 달인썰(SSULCINE 시네마틱 골격)
    case "sajangssul":   // 사장썰(SSULCINE 시네마틱 골격)
    case "openjunbissul": // 빈가게썰(SSULCINE 시네마틱 골격)
    case "sonnimssul":   // 목격썰(SEOLEMON 골격)
    case "jeongssul":    // 정썰(SSULLETTER 감성 골격)
    case "sajangtokssul": // 사장솔직썰(SEOLEMON 골격)
    case "golmoktoonssul": // 골목툰썰(SEOLEMON 일러 골격·카툰)
    case "jangsacutssul": // 한컷장사썰(SEOLEMON 트리비아 골격·플랫 도해)
    case "gagessul":     // 가게일러썰(SEOLEMON 일러 골격·수채)
    case "jangsawebtoonssul": // 장사웹툰썰(SEOLEMON 일러 골격·웹툰)
    case "sagoilji": {   // 현장일지썰(SEOLEMON 흑백 다큐 골격)
      const header = obj(d.header);
      if (header.themeLocked === true) {
        clone.header = header;
        return clone as T;
      }
      header.title = theme.name;
      header.bg = theme.barColor;
      header.line = theme.borderColor;
      header.emoji = theme.emoji;
      header.accent = theme.accentColor;
      clone.header = header;
      return clone as T;
    }
    case "listicle": {
      const t = obj(d.theme);
      t.colorHeader = theme.barColor;
      t.colorLogo = theme.accentColor;
      t.colorTitleBar = theme.borderColor;
      clone.theme = t;
      clone.logo = theme.name;
      return clone as T;
    }
    case "ranking":
    case "ranklist": { // 썰순위(list 모드) — 동일 RankingTheme 필드
      const t = obj(d.theme);
      t.colorBg = theme.barColor;
      t.colorHeader = theme.borderColor;
      t.colorLogo = theme.accentColor;
      t.colorTop = theme.accentColor;
      clone.theme = t;
      clone.logo = theme.name;
      return clone as T;
    }
    case "kchat": { // 톡썰 — ChatTheme 필드 재색
      const t = obj(d.theme);
      t.colorBg = theme.barColor;
      t.colorBar = theme.borderColor;
      t.colorBubbleR = theme.accentColor;
      clone.theme = t;
      clone.logo = theme.name;
      return clone as T;
    }
    case "versus": {
      const t = obj(d.theme);
      t.colorBg = theme.barColor;
      t.colorAccent = theme.accentColor;
      clone.theme = t;
      clone.logo = theme.name;
      return clone as T;
    }
    case "gunrimbo":
    case "gwajang":
    case "shonen":
    case "shojo": {
      const t = obj(d.theme);
      t.bg = theme.barColor;
      t.accent = theme.accentColor;
      t.outline = theme.borderColor;
      clone.theme = t;
      return clone as T; // 군림보/과장형/소년·순정만화 모두 렌더에 채널명 없음(이름은 카탈로그만)
    }
    case "shomaru": {
      const title = obj(d.title);
      const underline = obj(title.underline);
      title.accentColor = theme.accentColor;
      underline.color = theme.borderColor;
      title.underline = underline;
      clone.title = title;
      clone.background = theme.barColor;
      return clone as T; // 숏마루도 렌더에 채널명 없음
    }
    case "community": {
      const t = obj(d.theme);
      t.headerBg = theme.barColor;
      t.tagBg = theme.borderColor;
      t.commentName = theme.accentColor;
      clone.theme = t;
      clone.brand = theme.name;
      return clone as T;
    }
    case "board": {
      const t = obj(d.theme);
      t.headerBg = theme.barColor;
      t.headerFg = theme.accentColor;
      t.titleBg = theme.borderColor;
      clone.theme = t;
      clone.brand = theme.name;
      return clone as T;
    }
    case "health": {
      const t = obj(d.theme);
      t.yellow = theme.accentColor;
      t.red = theme.borderColor;
      clone.theme = t;
      return clone as T; // 건강썰은 헤더/채널명 없음
    }
    default:
      return data;
  }
}
